import React, { useState, useContext, useEffect, useMemo, useRef } from "react";
import { Tooltip } from "../Tooltip";
import styles from "./askTamarai.module.css";
import { useToast, ToastType } from "../../contexts/toast-context";
import LayoutContainer from "../LayoutContainer";
import BackArrowSVGComponent from "../svg-component/backArrow";
import DoneIcon from "../../assets/done.svg";
import DeletedIcon from "../../assets/deleted.svg";
import { AuthContext, AUTH0_CLIENT_ID } from "../../contexts/auth-context";
import { useAsyncFn } from "../../hooks/use-async-fn";
import { useHistory, useLocation } from "react-router-dom";
import CustomTable from "../custom-table";
import styled, { keyframes } from "styled-components";
import { FaArrowUp, FaPen, FaRedo } from "../../onam/icons"; // ONAM glyphs (was react-icons/fa)
import ColHeader from "../ColHeader";
import { MdTextFields, MdDataObject } from "../../onam/icons"; // ONAM glyphs (was react-icons/md)
import ExpandIcon from "../../assets/ExpandIcon";
import SearchIcon from "../../assets/search.svg";
import PlusIcon from "../../assets/plusIcon.svg";
import PageNotFound from "../PageNotFound";
import { useBreadcrumb } from "../Breadcrumb/BreadcrumbContext";
import { useSidebar } from "../NewSideBar/SidebarContext";
import { MdOutlineAutorenew } from "../../onam/icons"; // ONAM glyphs (was react-icons/md)
import InfoPopup from "../InfoPopup";
import LimitsUsageBanner from "../LimitsUsageBanner";
import { formatDateTime, formatDateTimeParts } from "../../utils";
import {
  useLimitsUsage,
  QueryTypeUsage,
} from "../../hooks/use-limits-usage";
import { OI } from "../../onam/mark";
import { FEATURES } from "../../onam/features";

// ─── Styled Components ────────────────────────────────────────────────────────

const StyledAttributeTable = styled(CustomTable)<{ hasTabs?: boolean }>`
  && {
    width: 100%;
    height: auto;
    display: flex;
    flex-direction: column;
    border-radius: ${({ hasTabs }) => (hasTabs ? "0 0 10px 10px" : "10px")};
    border: 1px solid var(--Gray-4, #b7bec6);
    overflow: hidden;
    box-shadow: 0px 0px 15px 0px rgba(0, 0, 0, 0.05);

    .rt-table {
      flex: none;
      overflow-y: auto;
      overflow-x: hidden;
    }

    .rt-thead.-header {
      box-shadow: none;
      border-right: 1px solid var(--Gray-4, #b7bec6);
      border-bottom: 1px solid var(--Gray-4, #b7bec6);
      background: #f4f5f6;
      position: sticky;
      top: 0;
      z-index: 1;
    }

    .rt-thead .rt-th {
      padding: 0.8rem;
      border-right: 1px solid #b7bec6;
      white-space: normal;
      display: flex;
      justify-content: center;
      align-items: center;
      text-align: center;
      color: #333;
      font-family: "Montserrat", sans-serif;
      font-size: 14px;
      font-style: normal;
      font-weight: 700;
      line-height: normal;
    }

    /* AESTHETIC: alternating row stripes for scannability */
    .rt-tbody .rt-tr:nth-child(odd) {
      background-color: #ffffff;
    }
    .rt-tbody .rt-tr:nth-child(even) {
      background-color: #f9fafb;
    }
    .rt-tbody .rt-tr {
      border-bottom: 1px solid #b7bec6;
    }

    .rt-tbody .rt-td {
      border-right: 1px solid var(--Gray-4, #b7bec6);
      color: #333;
      text-align: center;
      justify-content: center;
      align-items: center;
      font-family: "Roboto", sans-serif;
      font-size: 12px;
      font-style: normal;
      font-weight: 400;
      line-height: normal;
      padding: 0.4rem 0.8rem;
      > * {
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
      }
    }

    .rt-noData {
      position: relative;
      background: transparent;
      bottom: -1.5rem;
      text-align: center;
    }

    * {
      scrollbar-width: thin;
      scrollbar-color: var(--active-bg-color) transparent;
    }
    *::-webkit-scrollbar {
      width: 4px;
      height: 4px;
      background: transparent;
    }
    *::-webkit-scrollbar-track {
      background: transparent;
      border: none;
      box-shadow: none;
    }
    *::-webkit-scrollbar-thumb {
      background-color: var(--active-bg-color);
      border-radius: 4px;
      border: none;
    }
    *::-webkit-scrollbar-button {
      display: none;
      width: 0;
      height: 0;
    }
  }
`;

const dropdownIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const dropdownOut = keyframes`
  from { opacity: 1; transform: translateY(0); }
  to   { opacity: 0; transform: translateY(6px); }
`;

const ModelButton = styled.button`
  font-size: 13px;
  font-family: Roboto;
  color: #333;
  border: none;
  padding: 0;
  background: transparent;
  cursor: pointer;
  outline: none;
  display: flex;
  align-items: center;
  gap: 4px;
  position: relative;
  white-space: nowrap;
`;

/* AESTHETIC: top accent border anchors the dropdown visually to its trigger */
const ModelDropdown = styled.div<{ closing?: boolean }>`
  position: absolute;
  bottom: calc(100% + 6px);
  right: 0;
  background: #fff;
  border-top: 2px solid var(--Primary-Color-Shade-1);
  border-left: 1px solid #b7bec6;
  border-right: 1px solid #b7bec6;
  border-bottom: 1px solid #b7bec6;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 100;
  min-width: 160px;
  overflow: hidden;
  animation: ${({ closing }) => (closing ? dropdownOut : dropdownIn)} 0.15s ease
    forwards;
`;

const ModelOption = styled.div<{ selected?: boolean }>`
  padding: 8px 12px;
  font-size: 13px;
  font-family: Roboto;
  color: #333;
  background: ${({ selected }) => (selected ? "var(--Primary-Color-Shade-1-pale)" : "#fff")};
  font-weight: ${({ selected }) => (selected ? 600 : 400)};
  cursor: pointer;
  &:hover {
    background: var(--Primary-Color-Shade-1-pale);
  }
`;

/* AESTHETIC: 36px button, primary blue base, subtle active-press scale */
const SubmitButton = styled.button<{ active?: boolean }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: ${({ active }) => (active ? "var(--Primary-Color-Shade-1)" : "#d0d5db")};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ active }) => (active ? "pointer" : "not-allowed")};
  flex-shrink: 0;
  transition:
    background 0.15s,
    transform 0.1s;
  &:hover {
    background: ${({ active }) => (active ? "color-mix(in srgb, var(--Primary-Color-Shade-1) 80%, black)" : "#d0d5db")};
  }
  &:active {
    transform: ${({ active }) => (active ? "scale(0.95)" : "none")};
  }
`;

const StopButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: var(--Primary-Color-Shade-1);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition:
    background 0.15s,
    transform 0.1s;
  &:hover:not(:disabled) {
    background: color-mix(in srgb, var(--Primary-Color-Shade-1) 80%, black);
  }
  &:active:not(:disabled) {
    transform: scale(0.95);
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

/* AESTHETIC: pill badge for Query / Response / Status / Result turn labels */
const TurnLabel = styled.span<{
  variant?: "query" | "response" | "status" | "result";
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  min-width: 76px;
  border-radius: 4px;
  padding: 2px 9px;
  font-size: 11px;
  font-weight: 500;
  font-family: Roboto, sans-serif;
  letter-spacing: 0.02em;
  background: ${({ variant }) =>
    variant === "response" || variant === "result"
      ? "#eef6ee"
      : variant === "status"
        ? "#f4f4f4"
        : "var(--Primary-Color-Shade-1-pale)"};
  color: ${({ variant }) =>
    variant === "response" || variant === "result"
      ? "#2a7d3f"
      : variant === "status"
        ? "#6f7a85"
        : "var(--Primary-Color-Shade-1)"};
`;

// ─── Component ────────────────────────────────────────────────────────────────

const DiscoverAi = () => {
  const {
    getAttributes,
    getquery,
    updatequery,
    deletequery,
    getBucketNames,
    user,
    getgenerativeHistory,
    putqueryAI,
    draftNLQ,
    validateNLQ,
    executeNLQ,
    postIntermediateQuery,
    getCampaignNames,
    putCampaignName,
    getLlmModels,
    getQueryById,
    getConfig,
    appMetadata,
    applicationId,
  } = useContext(AuthContext);
  const history = useHistory();
  const location = useLocation<{ viewOnly?: boolean }>();
  const { setBreadcrumbItems } = useBreadcrumb();
  const { mainMenuItems, setActiveSubItem, setIsSideBarMenuDisabled } =
    useSidebar();
  const { showToast: showToastBase, dismissAll: dismissAllToasts } = useToast();
  const ASK_TAMARAI_TOAST_MS = 60_000;
  const showToast = (message: string, type?: ToastType) =>
    showToastBase(message, type, ASK_TAMARAI_TOAST_MS);

  // Ask MITRA toasts run much longer than the app default (60s vs 4s) so they
  // don't get missed mid-query, but that means they'd otherwise keep following
  // the user onto unrelated pages. Clear them once this page is left.
  useEffect(() => {
    return () => dismissAllToasts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [filter_query] = useState<any>(undefined);
  const [pageKeyStack, setPageKeyStack] = useState<any[]>([undefined]);
  const rowsPerPage = 8;

  var sessionFederationId = localStorage.getItem("federationkey");
  var organization_name = localStorage.getItem("organizationName");
  var fedId = sessionFederationId;

  // FIX: Read localStorage directly inside useMemo instead of via a var.
  // Previously, a duplicate `var sessionOrganizationId` declaration later
  // in the function body (after the userRole line) caused hoisting to
  // reset the variable, and since it was a plain var rather than reactive
  // state, useMemo never re-ran for it. Reading localStorage directly here
  // means appMetadata is the only reactive dependency, and the memo
  // correctly re-evaluates when context resolves after mount.
  const orgId = useMemo(() => {
    return (
      localStorage.getItem("organizationkey") ||
      (Object.values(appMetadata || {}) as any[])[0]?.organization
    );
  }, [appMetadata]);

  // Org-level list of query objectives the backend has enabled - Ask MITRA
  // only ever runs the "activation" one (see handleAISubmit below), even
  // when the org also has others (e.g. "enrichment") available elsewhere.
  const getConfigFn = useAsyncFn({
    promiseFn: getConfig,
    organizationId: orgId,
  });
  const availableQueryObjectives: string[] =
    getConfigFn.data?.message?.query_objectives_available ?? [];

  // AskTamarai's NL-query flow always submits objective: "activation"
  // (see handleAISubmit below) - there's no objective picker here, so usage
  // only ever needs to be checked for that one fixed query objective.
  const { fetchUsage: fetchLimitsUsageData, loading: limitsUsageLoading } =
    useLimitsUsage();
  const [objectiveUsage, setObjectiveUsage] = useState<
    QueryTypeUsage | undefined
  >(undefined);

  // FR-20 Task 1 AC8: block submission proactively once the org has used up
  // its query quota for this objective, instead of only surfacing the
  // backend's rejection after a wasted round trip. There's no fixed set of
  // physical tables here (the NL query plan resolves them dynamically), so
  // unlike NewQuery this can only gate on the org-level limit, not
  // per-table ones.
  const orgQueryLimitReached =
    !!objectiveUsage?.query_limit_usage &&
    objectiveUsage.query_limit_usage.query_count >=
      objectiveUsage.query_limit_usage.query_limit;

  useEffect(() => {
    if (!fedId || !orgId) {
      setObjectiveUsage(undefined);
      return;
    }
    let cancelled = false;
    fetchLimitsUsageData(["activation"], {
      organizationId: orgId,
      federationId: fedId,
    }).then((results) => {
      if (!cancelled) setObjectiveUsage(results[0]?.usage);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fedId, orgId]);

  // state management starts
  const [postContent, setPostContent] = useState("");
  const [showAIResult, setShowAIResult] = useState(false);
  const [, setError] = useState(false);
  const [showPending, setPending] = useState(false);
  const [draftData, setDraftData] = useState<any>(null);
  const [draftModalOpen, setDraftModalOpen] = useState(false);
  const [editedSql, setEditedSql] = useState("");
  const [editedQuery, setEditedQuery] = useState("");
  const [isDraftEditing, setIsDraftEditing] = useState(false);
  const [queryStopped, setQueryStopped] = useState(false);
  const [pendingStop, setPendingStop] = useState(false);
  const [locallyStoppedQueryId, setLocallyStoppedQueryId] = useState<
    string | null
  >(null);

  const [currentInputText, setCurrentInputText] = useState<any>(undefined);
  const [Message, setMessage] = useState();
  const followUpJustCompleted = useRef(false);
  const queryInputRef = useRef<HTMLInputElement>(null);
  const conversationBottomRef = useRef<HTMLDivElement>(null);
  const [conversationLimitReached, setConversationLimitReached] =
    useState(false);
  const [currentQueryId, setCurrentQueryId] = useState<any>(undefined);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const isRefreshingRef = useRef(false);
  const autoRefreshInProgressRef = useRef(false);
  const autoRefreshFnRef = useRef<(() => Promise<void>) | null>(null);
  const [refreshInfoByQueryId, setRefreshInfoByQueryId] = useState<
    Record<string, { completion: number; refreshedAt: number }>
  >({});
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelConfirmQueryId, setCancelConfirmQueryId] = useState<string | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [inputValue, setInputValue] = useState({ id: "", name: "" });
  const campaignDropdownRef = useRef<HTMLDivElement>(null);
  const upperScrollableRef = useRef<HTMLDivElement>(null);
  const [isCampaignAdded, setIsCampaignAdded] = useState(false);
  const [, setShowTickIcon] = useState(false);
  const [campaignNames, setCampaignNames] = useState([]);
  const [showPlusIcon, setShowPlusIcon] = useState(false);
  const [llmModels, setLlmModels] = useState<any[]>([]);
  const [selectedModel, setSelectedModel] = useState<any>(null);
  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
  const [modelDropdownClosing, setModelDropdownClosing] = useState(false);
  const closeModelDropdown = () => {
    setModelDropdownClosing(true);
    setTimeout(() => {
      setModelDropdownOpen(false);
      setModelDropdownClosing(false);
    }, 150);
  };
  const [activeTab, setActiveTab] = useState(0);
  const [tableCollapsed, setTableCollapsed] = useState(false);
  const [campaignCollapsed, setCampaignCollapsed] = useState(false);
  const [perBucketAttributes, setPerBucketAttributes] = useState<
    Record<string, Array<{ label: string; value: any }>>
  >({});
  const [attributesLoading, setAttributesLoading] = useState(false);
  const [isDraftLoading, setIsDraftLoading] = useState(false);
  const [isExecuteLoading, setIsExecuteLoading] = useState(false);
  const [isExecuteNLQFulfilled, setIsExecuteNLQFulfilled] = useState(false);
  const [isConversationDraft, setIsConversationDraft] = useState(false);
  const [processingWordIdx, setProcessingWordIdx] = useState(0);
  const [draftError, setDraftError] = useState<string | null>(null);

  const getCampaignNamesFn = useAsyncFn({ deferFn: getCampaignNames });
  const putCampaignNameFn = useAsyncFn({ deferFn: putCampaignName });
  const getLlmModelsFn = useAsyncFn({ deferFn: getLlmModels });
  const getBucketNamesFn = useAsyncFn({ deferFn: getBucketNames });

  var userMatchId = localStorage.getItem("userMatchId");
  var userBuckets = localStorage.getItem("bucketids");
  var conBuckets = localStorage.getItem("bucketids");
  const { given_name, family_name } = user || {};
  var user_name = given_name + " " + family_name;

  const selectedBucketObjects: Array<{
    bucket_id: string;
    organization_name: string;
    external_table_name: string;
    bucket_name: string;
  }> = (() => {
    try {
      const raw = localStorage.getItem("selectedBucketObjects");
      const parsed = JSON.parse(raw || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  })();

  // Narrows LimitsUsageBanner down to just the datasets selected on this
  // page, same as NewQuery's selectedTables. Paired with org name, not table
  // name alone - the same physical_table_name can be contributed by more
  // than one org in a federation (see use-limits-usage's TableUsage
  // comment), so matching on table name only would also pull in an
  // unrelated org's usage row.
  const selectedTables = selectedBucketObjects
    .filter((b) => b.external_table_name)
    .map((b) => ({
      organizationName: b.organization_name,
      tableName: b.external_table_name,
    }));

  const [storedPermissions] = useState(() =>
    JSON.parse(localStorage.getItem("userPermissions") || "{}"),
  );
  const fedSpecificMenuLabels = JSON.parse(
    localStorage.getItem("fedSpecificMenuLabels") || "{}",
  );
  const campaignReportsLabel =
    fedSpecificMenuLabels?.campaign_reports || "Audience Details";
  const campaignPlanningLabel =
    fedSpecificMenuLabels?.campaign_planning || "Audience Curation";

  const viewOnly = location.state?.viewOnly === true;
  const historyIdentifier = viewOnly ? undefined : userMatchId;


  const isViewOnly =
    viewOnly ||
    !userMatchId ||
    !conBuckets ||
    (() => {
      try {
        return JSON.parse(conBuckets).length === 0;
      } catch {
        return true;
      }
    })();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const sideBarMenuToHighlight = mainMenuItems
      .flatMap((item) => item.subMenu)
      .find((subItem) =>
        subItem.urls.some((url) => url.includes("/ask-tamarai")),
      )?.label;
    setActiveSubItem(sideBarMenuToHighlight);
    setIsSideBarMenuDisabled({
      fed: false,
      cp: false,
      cr: false,
      pr: false,
      ah: false,
      ca: false,
      dm: false,
    });
    setBreadcrumbItems([
      { label: "Discover", path: "/entry-hub?console=Discover" },
      { label: "Curation", path: "/federation-selection" },
      { label: "Federation Selection", path: "/federation-selection" },
      {
        label: fedSpecificMenuLabels?.campaign_planning || "Audience Curation",
        path: "/campaign-planning",
      },
      { label: "Ask Mitra", path: "/ask-tamarai" },
    ]);
    if (isViewOnly) setTableCollapsed(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePastQueries = () => history.push("/campaign-reports");

  const userRole = localStorage.getItem("userRole");
  // FIX: removed the duplicate `var sessionOrganizationId` declaration
  // that previously lived here — it was the root cause of orgId being
  // undefined, because var hoisting made the earlier useMemo read an
  // undefined value on the first render pass.

  var FilterQueryId;
  FilterQueryId = filter_query;

  const getQueriesFn = useAsyncFn({
    deferFn: getquery,
    federationId: fedId,
    organizationId: orgId,
    page_size: rowsPerPage,
    scan_index_forward: false,
    role: userRole,
    include_child_queries: true,
  });
  const getGenerativeHistoryFn = useAsyncFn({
    promiseFn: getgenerativeHistory,
    federationId: fedId,
    organizationId: orgId,
    Identifier: historyIdentifier,
    query_id: FilterQueryId,
    page_size: 7,
    scan_index_forward: false,
    role: userRole,
    include_child_queries: true,
  });
  const [, setTableData] = useState(getQueriesFn.data?.items);
  const putqueryAIFn = useAsyncFn({ deferFn: putqueryAI });
  const deleteQueryFn = useAsyncFn({ deferFn: deletequery });
  const postIntermediateQueryFn = useAsyncFn({
    deferFn: postIntermediateQuery,
  });

  var buckets = ((): string => {
    try {
      const parsed = JSON.parse(userBuckets || "[]");
      return Array.isArray(parsed) ? parsed.join(",") : "";
    } catch {
      return "";
    }
  })();

  useEffect(() => {
    // FIX: guard on orgId so we never fire API calls with undefined.
    // The effect also re-runs when orgId resolves (via [orgId] dep below).
    if (!orgId) return;

    const fetchCampaignData = async () => {
      try {
        const result = await getCampaignNamesFn.run({ orgId });
        const campaigns =
          result?.Items?.map((item) => ({ id: item.id, name: item.name })) ??
          [];
        setCampaignNames(campaigns);
      } catch (error) {
        console.error("Error fetching campaign data:", error);
      }
    };

    const fetchLlmModels = async () => {
      try {
        const result = await getLlmModelsFn.run({ organizationId: orgId });
        const providers = Array.isArray(result?.providers)
          ? result.providers
          : [];
        const models = providers.flatMap((p) =>
          (p.models ?? []).map((m) => ({
            provider: p.provider,
            model: m,
            displayName: m
              .replace(/^[a-z-]+\//i, "")
              .replace(/-/g, " ")
              .replace(/\b\w/g, (c) => c.toUpperCase()),
            maxTurns: p.max_nlq_conversation_turns ?? null,
          })),
        );
        setLlmModels(models);
        if (models.length > 0) setSelectedModel(models[0]);
      } catch (error) {
        console.error("Error fetching LLM models:", error);
      }
    };

    if (!viewOnly) {
      fetchCampaignData();
    }
    fetchLlmModels();
    // FIX: depend on orgId so this re-runs if appMetadata resolves late
  }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        campaignDropdownRef.current &&
        !campaignDropdownRef.current.contains(e.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const activeBucketId = selectedBucketObjects[activeTab]?.bucket_id;
  const resultvalues =
    (activeBucketId && perBucketAttributes[activeBucketId]) || [];

  const handleInputChange = (e) => {
    setIsCampaignAdded(false);
    setShowTickIcon(false);
    setShowPlusIcon(e.target.value.length > 0);
    setInputValue({ id: "", name: e.target.value });
    setIsDropdownOpen(e.target.value.length > 0);
  };

  const handleAISubmit = async (model?: any, inputTextOverride?: string) => {
    // Resolve orgId fresh at call time from every possible source
    const resolvedOrgId =
      localStorage.getItem("organizationkey") ||
      localStorage.getItem("organizationId") ||
      localStorage.getItem("orgId") ||
      localStorage.getItem("organization_id") ||
      (Object.values(appMetadata || {}) as any[])[0]?.organization ||
      (Object.values(appMetadata || {}) as any[])[0]?.organization_id ||
      (Object.values(appMetadata || {}) as any[])[0]?.org_id;

    if (!resolvedOrgId) {
      console.error(
        "orgId could not be resolved — localStorage keys:",
        Object.keys(localStorage),
      );
      console.error("appMetadata:", JSON.stringify(appMetadata));
      setError(true);
      return;
    }

    const queryText = inputTextOverride ?? postContent;
    if (!queryText.length) return;
    const selectedMod = model ?? selectedModel;

    setShowAIResult(true);
    setTableCollapsed(true);
    setCurrentInputText(queryText);
    setPostContent("");
    setMessage(undefined);
    setIsExecuteNLQFulfilled(false);
    setConversationLimitReached(false);
    setQueryStopped(false);
    setPendingStop(false);
    setLocallyStoppedQueryId(null);
    setDraftError(null);
    putqueryAIFn.init();
    // If a previous query is selected, continue in that conversation context
    // Uses the same draft → validate → execute flow as fresh queries
    if (selectedQueryId) {
      setShowAIResult(false);
      setIsConversationDraft(true);
      setIsDraftLoading(true);
      try {
        const body = {
          user_name: user_name,
          federation_id: fedId,
          inquirer_organization_id: resolvedOrgId,
          inquirer_bucket_id: selectedInquirerBucketId ?? "",
          contributor_bucket: (() => {
            try {
              const p = JSON.parse(conBuckets);
              return Array.isArray(p) ? p : [];
            } catch {
              return [];
            }
          })(),
          identifier: "",
          canonical_identity_type: userMatchId,
          objective: "activation",
          llm_provider: selectedMod?.provider,
          llm_model: selectedMod?.model,
          nl_query_params: {
            input_text: queryText,
          },
          campaign_name: inputValue?.name,
          campaign_id: inputValue?.id,
          client_id: AUTH0_CLIENT_ID,
          organization_id: resolvedOrgId,
          override_empty_predicate: true,
          conversation_id: selectedConversationId,
        };
        const result = await draftNLQ({ args: { body, organizationId: resolvedOrgId, applicationId } });
        setDraftData(result);
        setEditedSql(result?.sql_preview ?? "");
        setEditedQuery(queryText);
        setDraftModalOpen(true);
      } catch (err: any) {
        const msg = err?.message || "Failed to generate query. Please try again.";
        setPostContent(currentInputText ?? "");
        setIsConversationDraft(false);
        setDraftError(msg);
      } finally {
        setIsDraftLoading(false);
      }
      return;
    }

    await getBucketNamesFn.run({
      federationId: fedId,
      IdentifierType: userMatchId,
      organizationId: orgId,
    });

    if (true) {
      const body = {
        user_name: user_name,
        federation_id: fedId,
        inquirer_organization_id: orgId,
        inquirer_bucket_id: "",
        contributor_bucket: (() => {
          try {
            const p = JSON.parse(conBuckets);
            return Array.isArray(p) ? p : [];
          } catch {
            return [];
          }
        })(),
        identifier: "",
        canonical_identity_type: userMatchId,
        objective: "activation",
        llm_provider: selectedMod?.key,
        llm_model: selectedMod?.default_model,
        nl_query_params: {
          input_text: queryText,
        },
        campaign_name: inputValue?.name,
        campaign_id: inputValue?.id,
        client_id: AUTH0_CLIENT_ID,
        organization_id: orgId,
        override_empty_predicate: true,
      };
      try {
        if (
          !campaignNames.some((campaign) => campaign.name === inputValue?.name)
        ) {
          try {
            const response = await putCampaignNameFn.run({
              body: {
                name: inputValue?.name,
                federation_id: sessionFederationId,
                organization_id: orgId,
              },
              organizationId: orgId,
            });
            if (response?.id) {
              setInputValue((prev) => ({
                ...prev,
                id: response.id,
              }));
              body.campaign_id = response.id;
            }
          } catch {
            // campaign may already exist — proceed with query anyway
          }
        }
        // await putqueryAIFn.run({ body, fedId, organizationId: orgId });
        setIsDraftLoading(true);
        try {
          const result = await draftNLQ({
            args: { body, organizationId: orgId, applicationId },
          });
          setDraftData(result);
          setEditedSql(result?.sql_preview ?? "");
          setEditedQuery(queryText);
          setDraftModalOpen(true);
        } catch (err: any) {
          const msg = err?.message || "Failed to generate query. Please try again.";
          setDraftError(msg);
        } finally {
          setIsDraftLoading(false);
        }
      } catch {
        // error is handled via putqueryAIFn.isRejected
      }
    } else {
    }
  };

  const handleCancelDraft = () => {
    setDraftModalOpen(false);
    setDraftData(null);
    setEditedSql("");
    setEditedQuery("");
    setIsDraftEditing(false);
    // Restore the original query text so the user can tweak and resubmit
    setPostContent(currentInputText ?? "");
    if (isConversationDraft) {
      // Conversation follow-up: just reset draft state; leave conversation view intact
      setIsConversationDraft(false);
      setCurrentInputText(undefined);
    } else {
      // Fresh query: reset all in-progress query display state
      setShowAIResult(false);
      setCurrentInputText(undefined);
      setMessage(undefined);
      setIsExecuteNLQFulfilled(false);
      setConversationLimitReached(false);
      setQueryStopped(false);
      setPendingStop(false);
      setLocallyStoppedQueryId(null);
      putqueryAIFn.init();
    }
  };

  const handleConfirmExecute = async () => {
    setDraftModalOpen(false);
    setIsExecuteLoading(true);
    try {
      // Step 1: validate the (potentially edited) SQL before executing
      await validateNLQ({
        args: {
          draft_id: draftData?.draft_id,
          input_text: editedSql,
          organizationId: orgId,
          applicationId,
        },
      });

      // Step 2: execute
      const data = await executeNLQ({
        args: {
          draft_id: draftData?.draft_id,
          conversation_id: draftData?.conversation_id,
          organizationId: orgId,
          applicationId,
        },
      });

      if (isConversationDraft) {
        // Step 3a: conversation follow-up — append the new turn to selectedResults
        const allStrings = JSON.stringify(data || "").toLowerCase();
        if (allStrings.includes("maximum remembered")) {
          setConversationLimitReached(true);
        } else {
          setConversationLimitReached(false);
          setCurrentQueryId(data?.id);
          setSelectedQueryId(data?.id ?? null);
          setShowAIResult(false);
          setMessage(undefined);
          const newTurn: any = {
            prompt: currentInputText,
            query_id: data?.id,
            status: data?.status,
            result_string: data?.nl_query_params?.result_string,
            percentage_completion: data?.completion_percentage,
            query_exec_date: data?.created_at?.split("T")[0],
            query_exec_time: data?.created_at?.split("T")[1]?.replace("Z", ""),
          };
          if (selectedResults.length === 0 && Message) {
            const d = Message as any;
            setSelectedResults([
              {
                prompt: d.nl_query_params?.input_text,
                result_string: d.nl_query_params?.result_string,
                status: d.status,
                percentage_completion: d.completion_percentage,
                query_exec_date: d.created_at?.split("T")[0],
                query_exec_time: d.created_at?.split("T")[1]?.replace("Z", ""),
                turn: 1,
              },
              newTurn,
            ]);
          } else {
            setSelectedResults((prev) => [...prev, newTurn]);
          }
          followUpJustCompleted.current = true;
        }
      } else {
        // Step 3b: fresh query — update the single-result view
        setCurrentQueryId(data?.id);
        setMessage(data);
        setSelectedQueryId(data?.id ?? null);
        setIsExecuteNLQFulfilled(true);
      }
      getGenerativeHistoryFn.run({
        federationId: fedId,
        organizationId: orgId,
        Identifier: historyIdentifier,
        contributor_org_id: orgId,
        page_size: 7,
        scan_index_forward: false,
        role: userRole,
        include_child_queries: true,
      });
    } catch (err: any) {
      const msg = err?.message || "Failed to execute query. Please try again.";
      showToast(msg, "error");
    } finally {
      setIsExecuteLoading(false);
      setIsConversationDraft(false);
    }
  };

  const handleRegenerateDraft = () => {
    const query = editedQuery.trim();
    if (!query) return;
    setDraftModalOpen(false);
    setDraftData(null);
    setEditedSql("");
    setIsDraftEditing(false);
    handleAISubmit(selectedModel, query);
  };

  const handleSelect = (campaign) => {
    setShowTickIcon(true);
    setShowPlusIcon(false);
    setInputValue({ id: campaign.id, name: campaign.name });
    setIsDropdownOpen(false);
  };

  const handleKeypress = (e) => {
    if (e.keyCode === 13 && postContent?.trim() && inputValue?.name?.trim()) {
      handleAISubmit(selectedModel);
    }
  };

  useEffect(() => {
    if (pendingStop && currentQueryId) {
      setPendingStop(false);
      deleteQueryFn
        .run({ QueryId: currentQueryId, organizationId: orgId })
        .then(() => {
          setQueryStopped(true);
          setLocallyStoppedQueryId(currentQueryId);
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQueryId, pendingStop]);

  useEffect(() => {
    if (putqueryAIFn.isFulfilled) {
      setCurrentQueryId(putqueryAIFn.data?.id);
      setMessage(putqueryAIFn.data ? putqueryAIFn.data : null);
      setSelectedQueryId(putqueryAIFn.data?.id ?? null);

      getGenerativeHistoryFn.run({
        federationId: fedId,
        organizationId: orgId,
        Identifier: historyIdentifier,
        contributor_org_id: orgId,
        page_size: 7,
        scan_index_forward: false,
        role: userRole,
        include_child_queries: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [putqueryAIFn.isFulfilled, putqueryAIFn.data]);

  useEffect(() => {
    if (postIntermediateQueryFn.isFulfilled) {
      const data = postIntermediateQueryFn.data;
      const allStrings = JSON.stringify(data || "").toLowerCase();
      if (allStrings.includes("maximum remembered")) {
        setConversationLimitReached(true);
      } else {
        setConversationLimitReached(false);
        setCurrentQueryId(data?.id);
        setSelectedQueryId(data?.id ?? null);
        setShowAIResult(false);
        setMessage(undefined);
        setSelectedResults((prev) => {
          if (!prev.length) return prev;
          const updated = [...prev];
          updated[updated.length - 1] = {
            ...updated[updated.length - 1],
            query_id: data?.id,
            status: data?.status,
            result_string: data?.nl_query_params?.result_string,
            percentage_completion: data?.completion_percentage,
            query_exec_date: data?.created_at?.split("T")[0],
            query_exec_time: data?.created_at?.split("T")[1]?.replace("Z", ""),
          };
          return updated;
        });
        followUpJustCompleted.current = true;
      }
      getGenerativeHistoryFn.run({
        federationId: fedId,
        organizationId: orgId,
        Identifier: historyIdentifier,
        contributor_org_id: orgId,
        page_size: 7,
        scan_index_forward: false,
        role: userRole,
        include_child_queries: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postIntermediateQueryFn.isFulfilled, postIntermediateQueryFn.data]);

  console.log(Message, "message lenght outside");

  useEffect(() => {
    if (putqueryAIFn.isPending || postIntermediateQueryFn.isPending) {
      setPending(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [putqueryAIFn.isPending, postIntermediateQueryFn.isPending]);

  useEffect(() => {
    if (putqueryAIFn.isRejected) {
      console.log("putqueryAIFn.isFulfilled:", putqueryAIFn.isFulfilled);
      console.log("putqueryAIFn.data:", putqueryAIFn.data);
      console.log("message:", Message);
      setError(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [putqueryAIFn.isRejected]);

  useEffect(() => {
    if (!getGenerativeHistoryFn.data?.items || !selectedQueryId) return;
    const match = getGenerativeHistoryFn.data.items.find(
      (item: any) => item.id === selectedQueryId,
    );
    if (!match) return;

    if (match.conversation_id && !selectedConversationId) {
      setSelectedConversationId(match.conversation_id);
    }

    if (
      followUpJustCompleted.current &&
      match.past_prompts_per_conversation_id?.length > 0
    ) {
      const sorted = [...match.past_prompts_per_conversation_id].sort(
        (a, b) => (a.turn ?? 0) - (b.turn ?? 0),
      );
      setSelectedResults(sorted);
      followUpJustCompleted.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getGenerativeHistoryFn.data]);

  useEffect(() => {
    if (postIntermediateQueryFn.isRejected) {
      const errMsg =
        postIntermediateQueryFn.error?.message?.toLowerCase() || "";
      if (errMsg.includes("maximum remembered")) {
        setConversationLimitReached(true);
      } else {
        setError(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postIntermediateQueryFn.isRejected]);

  var multipleIdData = getQueriesFn.data?.items;

  const reloadQueries = async () => {
    try {
      const response = await getQueriesFn.run({
        federationId: fedId,
        organizationId: orgId,
        page_size: rowsPerPage,
        scan_index_forward: false,
        role: userRole,
        include_child_queries: true,
      });
      if (response) {
        setTableData(response.items);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const ActivequeryFn = useAsyncFn({
    deferFn: updatequery,
    reloadQueries,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (ActivequeryFn.data) {
      getQueriesFn.run();
    }
  }, [ActivequeryFn.data]); // eslint-disable-line react-hooks/exhaustive-deps

  var multiarr;
  var subRow;
  var multilist = [{ IsActive: "" }];
  var IsActive;
  var totalCount;
  var subtableRow;
  if (multipleIdData !== undefined && multipleIdData !== null) {
    for (let i = 0; i < multipleIdData.length; i++) {
      multiarr = multipleIdData[i];
      const countrate = multiarr.differential_privacy_count_total;
      const countDisplay = countrate.toLocaleString();
      totalCount = countDisplay.toLocaleString();
      const update = multiarr.updated_at;
      const identifier = multiarr.identifier;
      const bucketName = multiarr.inquirer_bucket_name;
      const objective = multiarr.objective;
      const audience_enrichment_count =
        multiarr.attribute_enriched_record_count;
      if (
        objective === "audienceMatching_enrichment" &&
        audience_enrichment_count !== undefined
      ) {
        const audienceCount = audience_enrichment_count.toLocaleString();
        totalCount = audienceCount;
      }
      const overallstatus = multipleIdData[i].status;
      const isDeleted = multipleIdData[i].is_deleted;
      const orgName =
        multipleIdData[i].inquirer_organization_name || organization_name;

      if (overallstatus === "FAILED") {
        totalCount = "0";
      }
      var subQueryId;
      var subUpdated_at;
      var totalSubCount;
      subtableRow = multiarr.executed_queries;
      var submultilist = [{ IsActive: "" }];
      if (overallstatus === "SUCCEEDED" && subtableRow !== null) {
        for (let k = 0; k < subtableRow.length; k++) {
          subRow = subtableRow[k];
          subQueryId = subRow.subquery_id;
          subUpdated_at = subRow.last_status_update;
          const subBucketName = subRow.contributor_bucket_name;
          const subOrgName = subRow.contributor_organization_name;
          const AudienceCount =
            subRow.differential_privacy_query?.type === "DP"
              ? subRow.differential_privacy_query.result_count
              : subRow.audience_result_count;
          const subAudienceCount = AudienceCount.toLocaleString();
          const submultiex = {
            countDisplay: "",
            QueryId: "",
            IsActive: "",
            overallstatus: "",
            iamCountDisplay: "",
            updated_at: "",
            identifier: "",
            bucketname: "",
            subOrgName: "",
          };
          totalSubCount = subAudienceCount;
          if (objective === "audienceMatching_enrichment") {
            totalSubCount = "NA";
          }
          Object.assign(submultiex, {
            countDisplay: totalSubCount,
            QueryId: subQueryId,
            IsActive: "subRow",
            overallstatus: overallstatus,
            iamCountDisplay: 0,
            updated_at: subUpdated_at,
            identifier: identifier,
            bucketname: subBucketName + " | " + subOrgName,
            subOrgName: subOrgName,
          });
          submultilist.push(submultiex);
        }
      }
      submultilist.shift();

      var multiex = {
        countDisplay: "",
        objective: "",
        QueryId: "",
        IsActive: "",
        overallstatus: "",
        isDeleted: "",
        updated_at: "",
        identifier: "",
        bucketname: "",
        inquirerorgName: "",
        executedQueries: [
          {
            countDisplay: "",
            subQueryId: "",
            IsActive: "",
            overallstatus: "",
            updated_at: "",
            identifier: "",
            bucketname: "",
          },
        ],
      };
      const QueryId = multipleIdData[i]?.id;
      IsActive = multipleIdData[i].marked_for_activation;
      Object.assign(multiex, {
        countDisplay: totalCount,
        QueryId: QueryId,
        objective: objective,
        IsActive: IsActive.toString(),
        overallstatus: overallstatus,
        isDeleted: isDeleted,
        iamCountDisplay: 0,
        updated_at: update,
        identifier: identifier,
        bucketname: bucketName,
        inquirerorgName: orgName,
        executedQueries: submultilist,
      });
      multilist.push(multiex);
    }
    multilist.shift();
  }

  useEffect(() => {
    if (getQueriesFn?.data) {
      setTableData(getQueriesFn.data?.items);
    }
  }, [getQueriesFn.data]);

  const [selectedResults, setSelectedResults] = useState<any[]>([]);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedQueryId, setSelectedQueryId] = useState("");
  const [selectedConversationId, setSelectedConversationId] = useState<
    string | null
  >(null);
  const [selectedInquirerBucketId, setSelectedInquirerBucketId] = useState<
    string | null
  >(null);

  useEffect(() => {
    conversationBottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [selectedResults, conversationLimitReached, showPending]);

  useEffect(() => {
    if (isDropdownOpen) {
      upperScrollableRef.current?.scrollTo({
        top: upperScrollableRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [isDropdownOpen]);

  const hasRunningQuery =
    putqueryAIFn.isPending ||
    postIntermediateQueryFn.isPending ||
    (typeof Message === "object" &&
      (Message as any)?.status?.toUpperCase() === "RUNNING") ||
    (selectedResults.length > 0 &&
      selectedResults.some((r) => r?.status?.toUpperCase() === "RUNNING"));

  const hasQueryError =
    (putqueryAIFn.isRejected || postIntermediateQueryFn.isRejected) &&
    !!currentInputText &&
    !conversationLimitReached;

  const isSelectedQueryCancelled =
    selectedStatus?.toUpperCase() === "CANCELLED" ||
    (!!selectedQueryId && selectedQueryId === locallyStoppedQueryId);
  const isSelectedQueryFailed =
    selectedStatus?.toUpperCase() === "FAILED" ||
    (selectedResults.length > 0 &&
      selectedResults[selectedResults.length - 1]?.status?.toUpperCase() ===
        "FAILED");
  const isStopInProgress = deleteQueryFn.isPending || pendingStop;

  // Keep the auto-refresh callback up to date on every render (callback-ref pattern).
  // Uses its own lock so it never touches isRefreshing state → no button flicker.
  autoRefreshFnRef.current = async () => {
    if (isRefreshingRef.current || autoRefreshInProgressRef.current) return;
    autoRefreshInProgressRef.current = true;
    try {
      if (
        typeof Message === "object" &&
        (Message as any)?.status?.toLowerCase() === "running" &&
        currentQueryId
      ) {
        const res = await getQueryById({
          args: { organizationId: orgId, query_id: currentQueryId },
        });
        if (res) setMessage(res);
      } else {
        const runningItem = [...selectedResults]
          .reverse()
          .find((r) => r?.status?.toUpperCase() === "RUNNING");
        if (runningItem) {
          const qid = runningItem.query_id ?? selectedQueryId;
          if (qid) {
            const res = await getQueryById({
              args: { organizationId: orgId, query_id: qid },
            });
            if (res) {
              setSelectedResults((prev) =>
                prev.map((r, i) =>
                  i === prev.length - 1
                    ? {
                        ...r,
                        status: res.status,
                        percentage_completion: res.completion_percentage,
                        result_string: res.nl_query_params?.result_string,
                        filter_str:
                          res.nl_query_params?.filter_str ?? r.filter_str,
                      }
                    : r,
                ),
              );
              setSelectedStatus(res.status ?? "");
            }
          }
        }
      }
    } catch {
      // ignore transient errors
    } finally {
      autoRefreshInProgressRef.current = false;
    }
  };

  // Auto-refresh every 5 s while a query is running
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (!hasRunningQuery || queryStopped || isStopInProgress || isSelectedQueryCancelled) return;
    const id = setInterval(() => autoRefreshFnRef.current?.(), 5000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRunningQuery, queryStopped, isStopInProgress, isSelectedQueryCancelled]);

  // Cycle through synonyms of "processing" while a query is running or loading
  const processingWords = [
    "Processing", "Thinking", "Analyzing", "Crunching", "Computing",
    "Working", "Querying", "Synthesizing", "Generating", "Researching",
  ];
  const isProcessing = hasRunningQuery || isDraftLoading || isExecuteLoading;
  useEffect(() => {
    if (!isProcessing) {
      setProcessingWordIdx(0);
      return;
    }
    const id = setInterval(() => {
      setProcessingWordIdx((prev) => (prev + 1) % processingWords.length);
    }, 1800);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProcessing]);

  useEffect(() => {
    const runningEntries: Array<{ queryId: string; completion: number }> = [];

    if (
      typeof Message === "object" &&
      Message &&
      (Message as any).status?.toLowerCase() === "running"
    ) {
      const queryId =
        (Message as any).id ?? currentQueryId ?? selectedQueryId;
      if (queryId) {
        runningEntries.push({
          queryId,
          completion: Number((Message as any).completion_percentage),
        });
      }
    }

    selectedResults.forEach((item) => {
      if (
        item.status?.toUpperCase() === "RUNNING" &&
        item.query_id
      ) {
        runningEntries.push({
          queryId: item.query_id,
          completion: Number(item.percentage_completion),
        });
      }
    });

    if (runningEntries.length === 0) return;

    setRefreshInfoByQueryId((prev) => {
      let next = prev;

      for (const { queryId, completion } of runningEntries) {
        const comp = Number.isFinite(completion) ? completion : 0;
        const existing = prev[queryId];

        // Stamp on first sight of a running query, or whenever its progress moves.
        if (!existing || existing.completion !== comp) {
          if (next === prev) next = { ...prev };
          next[queryId] = { completion: comp, refreshedAt: Date.now() };
        }
      }

      return next;
    });
  }, [Message, selectedResults, currentQueryId, selectedQueryId]);

  const renderLastRefreshedAt = (
    queryId?: string | null,
    withRightMargin = false,
  ) => {
    const info = queryId ? refreshInfoByQueryId[queryId] : undefined;
    if (!info) return null;

    return (
      <div
        className={`${styles.queryLastRefreshed}${
          withRightMargin ? ` ${styles.queryLastRefreshedRightMargin}` : ""
        }`}
      >
        Last refreshed {formatDateTimeParts(info.refreshedAt)?.time ?? "—"}
      </div>
    );
  };

  const addAllOrganizations = (
    query,
    result,
    status,
    queryId,
    conversationId?: string,
    inquirerBucketId?: string,
    campaignName?: string,
    campaignId?: string,
    historyData?: any,
  ) => {
    const turns =
      result?.length > 0
        ? result
        : query || historyData?.nl_query_params?.input_text
          ? [
              {
                prompt: query || historyData?.nl_query_params?.input_text,
                query_id: queryId,
                status,
                result_string: historyData?.nl_query_params?.result_string,
                percentage_completion: historyData?.completion_percentage,
                filter_str:
                  historyData?.filter_str ??
                  historyData?.nl_query_params?.filter_str,
                query_exec_date: historyData?.created_at?.split("T")[0],
                query_exec_time: historyData?.created_at
                  ?.split("T")[1]
                  ?.replace("Z", ""),
                turn: 1,
              },
            ]
          : [];

    setShowAIResult(false);
    setCurrentInputText(undefined);
    setDraftError(null);
    setMessage(undefined);
    setTableCollapsed(true);
    setSelectedResults(turns);
    setSelectedStatus(status);
    setSelectedQueryId(queryId);
    setSelectedConversationId(conversationId ?? null);
    setSelectedInquirerBucketId(inquirerBucketId ?? null);
    if (campaignName) {
      setInputValue({ id: campaignId ?? "", name: campaignName });
      setShowTickIcon(true);
      setShowPlusIcon(false);
    }
  };

  useEffect(() => {
    if (putqueryAIFn.isFulfilled && putqueryAIFn.data && putqueryAIFn.data) {
      getGenerativeHistoryFn.run({
        federationId: fedId,
        organizationId: orgId,
        Identifier: historyIdentifier,
        contributor_org_id: orgId,
        page_size: 8,
        scan_index_forward: false,
        role: userRole,
        include_child_queries: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [putqueryAIFn.isFulfilled, putqueryAIFn.data]);

  const goBack = () => {
    history.push("/campaign-planning");
  };

  useEffect(() => {
    if (userMatchId && selectedBucketObjects.length > 0) {
      setAttributesLoading(true);
      setActiveTab(0);
      Promise.all(
        selectedBucketObjects.map(async (bucket) => {
          try {
            const data = await getAttributes({
              args: {
                federationId: fedId,
                identifier: "",
                canonical_identity_type: userMatchId,
                buckets: bucket.bucket_id,
                organizationId: orgId,
              },
            });
            const attrItems: Array<{ label: string; value: any }> = Array.isArray(data)
              ? data.map((item: any) => ({
                  label: item.attribute_name || item.name || "",
                  value: Array.isArray(item.values)
                    ? item.values
                    : Object.values(item.values || {}),
                }))
              : Object.entries(Object.assign({}, data)).map(([key, val]: [string, any]) => ({
                  label: key,
                  value: Array.isArray(val?.values) ? val.values : (Array.isArray(val) ? val : []),
                }));
            return [
              bucket.bucket_id,
              attrItems,
            ] as [string, Array<{ label: string; value: any }>];
          } catch {
            return [bucket.bucket_id, []] as [
              string,
              Array<{ label: string; value: any }>,
            ];
          }
        }),
      ).then((results) => {
        setPerBucketAttributes(Object.fromEntries(results));
        setAttributesLoading(false);
      });
      reloadHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userMatchId, buckets]);

  const reloadHistory = () => {
    let historySize = 7;
    if (resultvalues !== undefined) {
      historySize = 7;
    }
    setPageKeyStack([undefined]);
    getGenerativeHistoryFn.run({
      federationId: fedId,
      organizationId: orgId,
      Identifier: historyIdentifier,
      contributor_org_id: orgId,
      page_size: historySize,
      scan_index_forward: false,
      role: userRole,
      include_child_queries: true,
    });
  };

  const previousHistory = () => {
    const newStack = pageKeyStack.slice(0, -1);
    const prevKey = newStack[newStack.length - 1];
    setPageKeyStack(newStack);
    getGenerativeHistoryFn.run({
      federationId: fedId,
      organizationId: orgId,
      Identifier: historyIdentifier,
      contributor_org_id: orgId,
      page_size: 7,
      last_key: prevKey,
      scan_index_forward: false,
      role: userRole,
      include_child_queries: true,
    });
  };

  const nextHistory = (nextDate) => {
    setPageKeyStack((prev) => [...prev, nextDate]);
    getGenerativeHistoryFn.run({
      federationId: fedId,
      organizationId: orgId,
      Identifier: historyIdentifier,
      contributor_org_id: orgId,
      page_size: 7,
      last_key: nextDate,
      scan_index_forward: false,
      role: userRole,
      include_child_queries: true,
    });
  };

  var genHistoryRaw = getGenerativeHistoryFn.data?.items;

  if (genHistoryRaw !== undefined) {
    var generativeHistory = genHistoryRaw?.filter(
      (genHistory) => genHistory.nl_query_params.input_text.length > 2,
    );
  }

  // ─── Deduplicate history: keep only the latest item per conversation_id ───
  // Each conversation produces multiple API items (one per turn). We only want
  // to show the most-recent turn per conversation in the sidebar list.
  const deduplicatedHistory: any[] = generativeHistory
    ? Object.values(
        generativeHistory.reduce(
          (acc: Record<string, any>, item: any) => {
            const convId = item.conversation_id;
            if (!convId) {
              // No conversation context — treat as a standalone entry
              acc[item.id] = item;
              return acc;
            }
            const existing = acc[convId];
            const existingTurnCount =
              existing?.past_prompts_per_conversation_id?.length ?? 0;
            const thisTurnCount =
              item.past_prompts_per_conversation_id?.length ?? 0;
            if (!existing || thisTurnCount >= existingTurnCount) {
              acc[convId] = item;
            }
            return acc;
          },
          {} as Record<string, any>,
        ),
      )
    : [];

  // ─── Utility: strip trailing "Query is RUNNING. Completion: X%." noise ───
  const stripRunningText = (str?: string) =>
    str
      ?.replace(/\.?\s*Query is RUNNING\.\s*Completion:\s*\d+%\.?/gi, "")
      .trim();

  const renderResultWithQueryIdLink = (text?: string) => {
    if (!text) return null;
    const parts = text.split(/(Query ID:\s*[0-9a-f-]{36})/i);
    return (
      <>
        {parts.map((part, i) => {
          const match = part.match(/^Query ID:\s*([0-9a-f-]{36})$/i);
          if (match) {
            return (
              <span key={i}>
                Query ID:{" "}
                <span
                  style={{
                    color: "var(--Primary-Color-Shade-1)",
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                  onClick={() =>
                    history.push(`/campaign-reports?queryid=${match[1]}`)
                  }
                >
                  {match[1]}
                </span>
              </span>
            );
          }
          return <span key={i}>{part}</span>;
        })}
      </>
    );
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return storedPermissions?.query === "no" ? (
    <PageNotFound />
  ) : (
    <>
      <LayoutContainer>
        <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes wordFadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

        <div className={styles.mainContainer}>
          {/* ── Left column ── */}
          <div className={styles.leftColumn}>
            <div className={styles.container} style={{ position: "relative" }}>
              <div className={styles.subcontainer}>
                {/* Header */}
                <div className={styles.headerContainer}>
                  <div
                    className={styles.header}
                    style={{ position: "relative" }}
                  >
                    <div
                      className={styles.leftSection}
                      style={{ cursor: "pointer", color: "var(--Primary-Color-Shade-1)" }}
                      onClick={goBack}
                    >
                      <BackArrowSVGComponent
                        style={{ height: "16px", width: "16px" }}
                      />
                      {/* AESTHETIC: letter-spacing on Montserrat nav text */}
                      <span
                        style={{
                          fontSize: "13px",
                          fontWeight: 600,
                          fontFamily: "Montserrat, sans-serif",
                          letterSpacing: "0.02em",
                        }}
                      >
                        Back
                      </span>
                    </div>
                    {/* AESTHETIC: slightly larger, letter-spaced page title */}
                    <span
                      className={styles.headerText}
                      style={{
                        position: "absolute",
                        left: "50%",
                        transform: "translateX(-50%)",
                        fontSize: "15px",
                        letterSpacing: "0.03em",
                      }}
                    >
                      Ask MITRA
                    </span>
                    <span
                      onClick={handlePastQueries}
                      style={{
                        cursor: "pointer",
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "var(--Primary-Color-Shade-1)",
                        textDecoration: "underline",
                        fontFamily: "Montserrat, sans-serif",
                        letterSpacing: "0.02em",
                      }}
                    >
                      Go to {campaignReportsLabel}
                    </span>
                  </div>
                </div>

                {/* Attrs + campaign wrapper: scrollable, shrinkable so input stays visible */}
                <div
                  ref={upperScrollableRef}
                  className={styles.upperScrollableSection}
                >
                  {/* Dataset Attributes section header */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "8px 20px 0px 20px",
                      flexShrink: 0,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "13px",
                          fontWeight: 600,
                          color: "#333",
                          fontFamily: "Montserrat, sans-serif",
                          letterSpacing: "0.02em",
                        }}
                      >
                        Dataset Attributes
                      </span>
                      <LimitsUsageBanner
                        loading={limitsUsageLoading}
                        usage={objectiveUsage}
                        selectedTables={selectedTables}
                        inline
                      />
                    </div>
                    {!isViewOnly && (
                      <button
                        onClick={() => setTableCollapsed((c) => !c)}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: "#6f7a85",
                          fontSize: "12px",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          padding: "2px 4px",
                        }}
                      >
                        {tableCollapsed ? "Show  ▾" : "Hide  ▴"}
                      </button>
                    )}
                  </div>

                  {availableQueryObjectives.length > 0 && (
                    <div
                      style={{
                        padding: "2px 20px 0px 20px",
                        fontSize: "11px",
                        color: "#9ca3af",
                        fontStyle: "italic",
                        flexShrink: 0,
                      }}
                    >
                      Only the Activation objective is used here
                      {availableQueryObjectives.length > 1 &&
                        ` (your organization also has ${availableQueryObjectives
                          .filter((o) => o?.toLowerCase() !== "activation")
                          .join(", ")} available for other query objective)`}
                      .
                    </div>
                  )}

                  {/* AESTHETIC: smoother cubic-bezier collapse */}
                  <div
                    className={styles.scrollableContent}
                    style={{
                      maxHeight: tableCollapsed ? "0" : "1000px",
                      overflow: "hidden",
                      transition:
                        "max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    <div
                      style={{
                        flex: 1,
                        minHeight: "132px",
                        display: "flex",
                        flexDirection: "column",
                        marginLeft: "20px",
                        marginTop:
                          selectedBucketObjects.length > 0 ? "16px" : "40px",
                        marginRight: "20px",
                        marginBottom: "40px",
                      }}
                    >
                      {selectedBucketObjects.length === 1 && (
                        <div className={styles.datasetHeading}>
                          <span className={styles.datasetHeadingOrg}>
                            <span style={{ fontWeight: 400 }}>Dataset: </span>
                            {selectedBucketObjects[0].bucket_name ||
                              selectedBucketObjects[0].organization_name}
                          </span>
                        </div>
                      )}
                      {selectedBucketObjects.length > 1 && (
                        <div className={styles.tabStrip}>
                          {selectedBucketObjects.map((bucket, i) => (
                            <button
                              key={bucket.bucket_id}
                              className={`${styles.tab} ${activeTab === i ? styles.tabActive : ""}`}
                              onClick={() => setActiveTab(i)}
                            >
                              <span className={styles.tabOrgName}>
                                {bucket.bucket_name || bucket.organization_name}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                      <StyledAttributeTable
                        list={attributesLoading ? [] : resultvalues}
                        pageSize={resultvalues.length}
                        noDataText="No attributes found"
                        hasTabs={selectedBucketObjects.length > 1}
                        columns={[
                          {
                            Header: (
                              <ColHeader
                                label="Attributes"
                                icon={<MdTextFields />}
                              />
                            ),
                            accessor: "label",
                            sortable: false,
                            minWidth: 200,
                            Cell: ({ value, original }) => (
                              <div
                                style={{ fontWeight: 500, fontSize: "15px" }}
                              >
                                <span>{value}</span>
                              </div>
                            ),
                          },
                          {
                            Header: (
                              <ColHeader
                                label="Values"
                                icon={<MdDataObject />}
                              />
                            ),
                            accessor: "value",
                            sortable: false,
                            minWidth: 600,
                            Cell: ({ value }) => {
                              const values = Array.isArray(value)
                                ? value
                                : value != null
                                  ? [value]
                                  : [];
                              return (
                                <div
                                  style={{
                                    fontWeight: 500,
                                    fontSize: "15px",
                                    display: "flex",
                                    alignItems: "center",
                                    overflowX: "auto",
                                    width: "100%",
                                  }}
                                >
                                  <span
                                    style={{
                                      padding: "0 0.5rem",
                                      margin: "0 auto",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {values.map((item, index) => (
                                      <React.Fragment key={index}>
                                        {index > 0 && <span> | </span>}
                                        <span>{item}</span>
                                      </React.Fragment>
                                    ))}
                                  </span>
                                </div>
                              );
                            },
                          },
                        ]}
                      />
                    </div>
                  </div>
                  {/* end scrollableContent */}
                </div>
                {/* end upperScrollableSection */}

                {/* Cohort Name — lives outside the overflow-clipped scroll area so its dropdown is visible */}
                {!isViewOnly && (
                  <div className={styles.campaignNameSection}>
                    <label
                      className={styles.askAiLabel}
                      style={{ whiteSpace: "nowrap", marginBottom: 0 }}
                    >
                      Cohort Name <span className={styles.required}>*</span>
                    </label>
                    <div
                      ref={campaignDropdownRef}
                      style={{ flex: 1, position: "relative" }}
                    >
                      {/* AESTHETIC: smoother cubic-bezier collapse */}
                      <div
                        style={{
                          maxHeight: campaignCollapsed ? "0" : "200px",
                          overflow: "hidden",
                          transition:
                            "max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                        }}
                      >
                        <div className={styles.nameFieldContainer}>
                          <div className={styles.nameField}>
                            <img
                              src={SearchIcon}
                              alt="Search"
                              className={styles.searchIcon}
                              onClick={() =>
                                setIsDropdownOpen(!isDropdownOpen)
                              }
                              style={{
                                filter:
                                  "invert(58%) sepia(7%) saturate(154%) hue-rotate(178deg) brightness(95%) contrast(89%)",
                                cursor: "pointer",
                              }}
                            />
                            <input
                              type="text"
                              placeholder="Enter Name"
                              value={inputValue?.name}
                              onChange={handleInputChange}
                              onBlur={() =>
                                setTimeout(
                                  () => setIsDropdownOpen(false),
                                  150,
                                )
                              }
                            />
                            {inputValue?.name && showPlusIcon && (
                                <Tooltip text="Create a new cohort" placement="top">
                                  <img
                                    src={PlusIcon}
                                    alt="Add"
                                    className={styles.plusIcon}
                                    onClick={() => {
                                      handleSelect({
                                        id: "",
                                        name: inputValue.name,
                                      });
                                      setIsCampaignAdded(true);
                                    }}
                                  />
                                </Tooltip>
                              )}
                            {isCampaignAdded && (
                              <img
                                src={DoneIcon}
                                alt="Done"
                                className={styles.doneIcon}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                      {isDropdownOpen && !campaignCollapsed && (
                        <div className={styles.dropdown}>
                          {campaignNames
                            .filter((campaign) =>
                              campaign.name
                                .toLowerCase()
                                .includes(inputValue?.name?.toLowerCase()),
                            )
                            .map((campaign) => (
                              <div
                                key={campaign.id}
                                className={styles.dropdownItem}
                                onClick={() => handleSelect(campaign)}
                              >
                                {campaign.name}
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => setCampaignCollapsed((c) => !c)}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "#6f7a85",
                        fontSize: "12px",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        padding: "2px 4px",
                        marginLeft: "auto",
                        flexShrink: 0,
                      }}
                    >
                      {campaignCollapsed ? "Show ▾" : "Hide ▴"}
                    </button>
                  </div>
                )}

                {/* Conversation / result area */}
                <div className={styles.queryResultOverlayMoved}>
                  {/* Selected conversation turns */}
                  {selectedResults.length > 0 || selectedStatus ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        padding: "0px 40px 10px 40px",
                        gap: "12px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                          marginTop: "8px",
                        }}
                      >
                        <button
                          onClick={() => {
                            setShowAIResult(false);
                            setSelectedResults([]);
                            setSelectedStatus("");
                            setSelectedQueryId(null);
                            setSelectedConversationId(null);
                            setSelectedInquirerBucketId(null);
                            setCurrentInputText(undefined);
                            setConversationLimitReached(false);
                            setMessage(undefined);
                            setDraftError(null);
                          }}
                          style={{
                            fontSize: "0.75rem",
                            fontFamily: "Roboto",
                            color: "#6f7a85",
                            background: "none",
                            border: "1px solid #b7bec6",
                            borderRadius: "6px",
                            padding: "3px 10px",
                            cursor: "pointer",
                          }}
                        >
                          {FEATURES.curationSkin ? <><OI n="close" size="1em" /> Clear</> : "✕ Clear"}
                        </button>
                      </div>

                      {selectedResults.map((item: any, idx: number) => (
                        <div
                          key={idx}
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "4px",
                            fontFamily: "Roboto",
                            color: "#333",
                            background: "var(--color-background-secondary)",
                            borderRadius: "8px",
                            padding: "10px 12px",
                          }}
                        >
                          {/* AESTHETIC: pill badge replaces bold "Query:" colon label */}
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              gap: "8px",
                              alignItems: "flex-start",
                            }}
                          >
                            <TurnLabel variant="query">Query</TurnLabel>
                            <span
                              style={{ fontSize: "0.875rem", fontWeight: 300 }}
                            >
                              {item.prompt}
                            </span>
                          </div>

                          {!(
                            conversationLimitReached &&
                            idx === selectedResults.length - 1 &&
                            item.status?.toUpperCase() === "RUNNING"
                          ) && (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "row",
                                gap: "8px",
                                alignItems: "flex-start",
                              }}
                            >
                              {/* AESTHETIC: pill badge */}
                              <TurnLabel variant="response">Response</TurnLabel>
                              {item.status?.toUpperCase() === "RUNNING" ? (
                                queryStopped ? (
                                  <span
                                    style={{
                                      fontSize: "0.875rem",
                                      fontWeight: 300,
                                      color: "#7a5c00",
                                      fontStyle: "italic",
                                    }}
                                  >
                                    This query was cancelled.
                                  </span>
                                ) : (
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: "6px",
                                    }}
                                  >
                                    {!isStopInProgress &&
                                      item.filter_str?.length > 0 && (
                                        <span
                                          style={{
                                            fontSize: "0.875rem",
                                            fontWeight: 300,
                                            whiteSpace: "pre-line",
                                          }}
                                        >
                                          {item.filter_str.join("\n")}
                                        </span>
                                      )}
                                    {isStopInProgress ? (
                                      <span style={{ fontSize: "0.875rem", fontWeight: 300, color: "#7a5c00" }}>
                                        Stopping query, please wait…
                                      </span>
                                    ) : (
                                      <span style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "0.875rem", fontWeight: 300 }}>
                                        <div className={styles.inlineLoadingSpinner} style={{ flexShrink: 0 }} />
                                        <span key={processingWordIdx} style={{ animation: "wordFadeIn 0.35s ease forwards", display: "inline-block" }}>
                                          {processingWords[processingWordIdx]}
                                        </span>
                                        {item.percentage_completion > 0 ? `… ${item.percentage_completion}% done.` : "…"}
                                      </span>
                                    )}
                                  </div>
                                )
                              ) : item.status?.toUpperCase() === "CANCELLED" ? (
                                <span
                                  style={{
                                    fontSize: "0.875rem",
                                    fontWeight: 300,
                                    color: "#7a5c00",
                                    fontStyle: "italic",
                                  }}
                                >
                                  This query was cancelled.
                                </span>
                              ) : (
                                <span
                                  style={{
                                    fontSize: "0.875rem",
                                    fontWeight: 300,
                                    whiteSpace: "pre-line",
                                  }}
                                >
                                  {renderResultWithQueryIdLink(
                                    stripRunningText(
                                      item.result_string ??
                                        item.nl_query_params?.result_string,
                                    ),
                                  ) || "No result available"}
                                </span>
                              )}
                              {item.status?.toUpperCase() === "RUNNING" &&
                                item.query_id &&
                                !isStopInProgress &&
                                !queryStopped &&
                                !isSelectedQueryCancelled && (
                                  <>
                                    <Tooltip text="Refresh to get latest results" placement="top">
                                    <button
                                      className={styles.queryRefreshBtn}
                                      disabled={isRefreshing}
                                      onClick={async () => {
                                        if (isRefreshingRef.current || autoRefreshInProgressRef.current) return;
                                        isRefreshingRef.current = true;
                                        setIsRefreshing(true);
                                        try {
                                          const res = await getQueryById({
                                            args: {
                                              organizationId: orgId,
                                              query_id:
                                                item.query_id ?? selectedQueryId,
                                            },
                                          });
                                          if (res) {
                                            setSelectedResults((prev) =>
                                              prev.map((r, i) =>
                                                i === prev.length - 1
                                                  ? {
                                                      ...r,
                                                      status: res.status,
                                                      percentage_completion:
                                                        res.completion_percentage,
                                                      result_string:
                                                        res.nl_query_params
                                                          ?.result_string,
                                                      filter_str:
                                                        res.nl_query_params
                                                          ?.filter_str ??
                                                        r.filter_str,
                                                    }
                                                  : r,
                                              ),
                                            );
                                            setSelectedStatus(res.status ?? "");
                                          }
                                        } catch {
                                          // ignore transient errors; user can retry
                                        } finally {
                                          isRefreshingRef.current = false;
                                          setIsRefreshing(false);
                                        }
                                      }}
                                    >
                                      <MdOutlineAutorenew
                                        size={13}
                                        style={{
                                          animation: isRefreshing
                                            ? "spin 0.8s linear infinite"
                                            : undefined,
                                          transition: "opacity 0.2s ease",
                                          opacity: isRefreshing ? 1 : 0.6,
                                        }}
                                      />
                                      {isRefreshing
                                        ? "Refreshing…"
                                        : "Refresh"}
                                    </button>
                                    </Tooltip>
                                    <Tooltip text={deleteQueryFn.isPending ? "Stopping…" : "Cancel this query"} placement="top">
                                    <button
                                      className={styles.queryCancelBtn}
                                      disabled={deleteQueryFn.isPending}
                                      onClick={() => {
                                        const queryId =
                                          (item.query_id ??
                                            currentQueryId ??
                                            selectedQueryId) as string | null ?? null;
                                        setCancelConfirmQueryId(queryId);
                                        setShowCancelConfirm(true);
                                      }}
                                    >
                                      {deleteQueryFn.isPending
                                        ? "Stopping…"
                                        : "Cancel"}
                                    </button>
                                    </Tooltip>
                                  </>
                                )}
                            </div>
                          )}

                          {item.status?.toUpperCase() === "RUNNING"
                            ? renderLastRefreshedAt(item.query_id)
                            : item.query_exec_date &&
                              item.query_exec_time && (
                                <div className={styles.queryExecTimestamp}>
                                  {formatDateTime(
                                    `${item.query_exec_date}T${item.query_exec_time}`,
                                  )}
                                </div>
                              )}
                        </div>
                      ))}

                      {/* Conversation follow-up: inline loading while draft/execute APIs run */}
                      {isConversationDraft && (isDraftLoading || isExecuteLoading) && currentInputText && (
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                          <div className={styles.leftResult}>
                            <TurnLabel variant="query" style={{ marginRight: "8px" }}>Query</TurnLabel>
                            <span style={{ fontSize: "0.875rem", fontWeight: 300, color: "#333" }}>
                              {currentInputText}
                            </span>
                          </div>
                          <div className={styles.inlineLoading}>
                            <div className={styles.inlineLoadingSpinner} />
                            <span className={styles.inlineLoadingText}>
                              <span key={processingWordIdx} style={{ animation: "wordFadeIn 0.35s ease forwards", display: "inline-block" }}>
                                {processingWords[processingWordIdx]}
                              </span>
                              {"…"}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    ""
                  )}

                  {/* AESTHETIC: on-palette blue info banner replaces warm yellow */}
                  {conversationLimitReached && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        padding: "10px 40px",
                        background: "var(--Primary-Color-Shade-1-pale)",
                        borderTop: "1px solid color-mix(in srgb, var(--Primary-Color-Shade-1-pale) 60%, #d7dbdf)",
                      }}
                    >
                      <span style={{ fontSize: "0.875rem", color: "#333" }}>
                        You've reached the maximum
                        {selectedModel?.maxTurns
                          ? ` ${selectedModel.maxTurns}`
                          : ""}{" "}
                        query turns for this conversation.
                      </span>
                      <button
                        onClick={() => {
                          setSelectedQueryId(null);
                          setSelectedConversationId(null);
                          setSelectedResults([]);
                          setSelectedStatus("");
                          setSelectedInquirerBucketId(null);
                          setConversationLimitReached(false);
                          setCurrentInputText(undefined);
                          setDraftError(null);
                        }}
                        style={{
                          fontSize: "0.8rem",
                          fontWeight: 600,
                          color: "var(--Primary-Color-Shade-1)",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          textDecoration: "underline",
                          padding: 0,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Start a new conversation
                      </button>
                    </div>
                  )}

                  {/* Cancelled query banner */}
                  {isSelectedQueryCancelled && !queryStopped && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        padding: "10px 16px",
                        background: "#fffbea",
                        border: "1px solid #f0d080",
                        borderLeft: "3px solid #c49a00",
                        borderRadius: "8px",
                        margin: "8px 0",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "0.875rem",
                          color: "#7a5c00",
                          flex: 1,
                        }}
                      >
                        This query was cancelled. Start a new chat to run a
                        fresh query.
                      </span>
                      <button
                        onClick={() => {
                          setSelectedResults([]);
                          setSelectedStatus("");
                          setSelectedQueryId(null);
                          setSelectedConversationId(null);
                          setSelectedInquirerBucketId(null);
                          setCurrentInputText(undefined);
                          setDraftError(null);
                          setPostContent("");
                          setTimeout(() => queryInputRef.current?.focus(), 0);
                        }}
                        style={{
                          fontSize: "0.8rem",
                          fontWeight: 600,
                          color: "#7a5c00",
                          background: "none",
                          border: "1px solid #f0d080",
                          borderRadius: "6px",
                          padding: "3px 10px",
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                          flexShrink: 0,
                        }}
                      >
                        New chat
                      </button>
                    </div>
                  )}

                  {/* Query stopped banner */}
                  {queryStopped && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        padding: "10px 16px",
                        background: "#fffbea",
                        border: "1px solid #f0d080",
                        borderLeft: "3px solid #c49a00",
                        borderRadius: "8px",
                        margin: "8px 0",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "0.875rem",
                          color: "#7a5c00",
                          flex: 1,
                        }}
                      >
                        Query was interrupted. You can clear and start a new
                        one.
                      </span>
                      <button
                        onClick={() => {
                          setQueryStopped(false);
                          setPendingStop(false);
                          setShowAIResult(false);
                          setCurrentInputText(undefined);
                          setPostContent("");
                          setMessage(undefined);
                          setSelectedResults([]);
                          setSelectedStatus("");
                          setSelectedQueryId(null);
                          setSelectedConversationId(null);
                          setSelectedInquirerBucketId(null);
                          setConversationLimitReached(false);
                          setPending(false);
                          setDraftError(null);
                          putqueryAIFn.init();
                        }}
                        style={{
                          fontSize: "0.8rem",
                          fontWeight: 600,
                          color: "#7a5c00",
                          background: "none",
                          border: "1px solid #f0d080",
                          borderRadius: "6px",
                          padding: "3px 10px",
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                          flexShrink: 0,
                        }}
                      >
                        Clear &amp; start over
                      </button>
                    </div>
                  )}

                  {/* Pending query text + inline loading indicator */}
                  {currentInputText && selectedResults.length === 0 && (
                    <div style={{ marginTop: "14px", display: "flex", flexDirection: "column", gap: "8px" }}>
                      <div className={styles.leftResult}>
                        {/* AESTHETIC: pill badge */}
                        <TurnLabel variant="query" style={{ marginRight: "8px" }}>
                          Query
                        </TurnLabel>
                        <span
                          style={{
                            fontSize: "0.875rem",
                            fontWeight: 300,
                            color: "#333",
                          }}
                        >
                          {currentInputText}
                        </span>
                      </div>
                      {(isDraftLoading || isExecuteLoading) && (
                        <div className={styles.inlineLoading}>
                          <div className={styles.inlineLoadingSpinner} />
                          <span className={styles.inlineLoadingText}>
                            <span key={processingWordIdx} style={{ animation: "wordFadeIn 0.35s ease forwards", display: "inline-block" }}>
                              {processingWords[processingWordIdx]}
                            </span>
                            {"…"}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Inline SQL review card (replaces InfoPopup modal) */}
                  {draftModalOpen && (
                    <div className={styles.sqlReviewCard}>
                      <div className={styles.sqlReviewHeader}>
                        <span className={styles.sqlReviewTitle}>
                          {isDraftEditing
                            ? "Edit your query"
                            : "Review generated query"}
                        </span>
                        <button
                          className={styles.sqlEditToggle}
                          onClick={() => setIsDraftEditing((v) => !v)}
                        >
                          {FEATURES.curationSkin ? (isDraftEditing ? <><OI n="close" size="1em" /> Cancel edit</> : <><OI n="pen" size="1em" /> Edit Query</>) : isDraftEditing ? "✕ Cancel edit" : "✎ Edit Query"}
                        </button>
                      </div>
                      <div className={styles.sqlReviewBody}>
                        {isDraftEditing ? (
                          <textarea
                            className={styles.draftQueryTextarea}
                            value={editedQuery}
                            onChange={(e) => setEditedQuery(e.target.value)}
                            spellCheck
                            placeholder="Describe what you want to query…"
                          />
                        ) : (
                          <pre className={styles.sqlPreviewPre}>{editedSql}</pre>
                        )}
                      </div>
                      <div className={styles.sqlReviewFooter}>
                        <button
                          className={styles.draftCancelBtn}
                          onClick={handleCancelDraft}
                        >
                          Discard
                        </button>
                        {isDraftEditing ? (
                          <button
                            className={styles.draftExecuteBtn}
                            onClick={handleRegenerateDraft}
                            disabled={!editedQuery.trim()}
                          >
                            Regenerate →
                          </button>
                        ) : (
                          <button
                            className={styles.draftExecuteBtn}
                            onClick={handleConfirmExecute}
                          >
                            Run Query →
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Waiting status */}
                  {currentInputText &&
                    showPending &&
                    selectedResults.length === 0 &&
                    !conversationLimitReached &&
                    !putqueryAIFn.isFulfilled &&
                    !putqueryAIFn.isRejected &&
                    !postIntermediateQueryFn.isFulfilled &&
                    !postIntermediateQueryFn.isRejected && (
                      <div className={styles.leftResult}>
                        {/* AESTHETIC: pill badge */}
                        <TurnLabel
                          variant="status"
                          style={{ marginRight: "8px" }}
                        >
                          Status
                        </TurnLabel>
                        {isStopInProgress ? (
                          <span style={{ fontSize: "0.875rem", fontWeight: 300, color: "#7a5c00" }}>
                            Stopping query, please wait…
                          </span>
                        ) : (
                          <span style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "0.875rem", fontWeight: 300 }}>
                            <div className={styles.inlineLoadingSpinner} style={{ flexShrink: 0 }} />
                            <span key={processingWordIdx} style={{ animation: "wordFadeIn 0.35s ease forwards", display: "inline-block" }}>
                              {processingWords[processingWordIdx]}
                            </span>
                            {"…"}
                          </span>
                        )}
                      </div>
                    )}

                  {/* AESTHETIC: left accent bar on error card, on-palette red tint */}
                  {(hasQueryError || putCampaignNameFn.isRejected || !!draftError) &&
                    currentInputText &&
                    !conversationLimitReached && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "10px",
                          padding: "10px 16px",
                          background: "#fdf2f2",
                          border: "1px solid #f0c4c4",
                          borderLeft: "3px solid #c0392b",
                          borderRadius: "8px",
                          margin: "8px 0",
                        }}
                      >
                        <img
                          src={DeletedIcon}
                          alt="failed"
                          style={{
                            width: "18px",
                            height: "18px",
                            flexShrink: 0,
                            marginTop: "1px",
                          }}
                        />
                        <span
                          style={{
                            fontSize: "0.875rem",
                            color: "#922b21",
                            flex: 1,
                          }}
                        >
                          {((msg) =>
                            msg.charAt(0).toUpperCase() + msg.slice(1))(
                            (putCampaignNameFn.isRejected
                              ? putCampaignNameFn.error?.message
                              : postIntermediateQueryFn.isRejected
                                ? postIntermediateQueryFn.error?.message
                                : draftError || putqueryAIFn.error?.message) ||
                              "Something went wrong while processing your query.",
                          )}{" "}
                          Please clear and try again.
                        </span>
                        <button
                          onClick={() => {
                            setShowAIResult(false);
                            setIsConversationDraft(false);
                            setCurrentInputText(undefined);
                            setPostContent("");
                            setMessage(undefined);
                            setDraftError(null);
                          }}
                          style={{
                            fontSize: "0.8rem",
                            fontWeight: 600,
                            color: "#922b21",
                            background: "none",
                            border: "1px solid #f0c4c4",
                            borderRadius: "6px",
                            padding: "3px 10px",
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                            flexShrink: 0,
                          }}
                        >
                          Clear &amp; start over
                        </button>
                      </div>
                    )}

                  {/* AI result (new query, not follow-up) */}
                  {showAIResult &&
                    (putqueryAIFn.isFulfilled ||
                      postIntermediateQueryFn.isFulfilled ||
                      isExecuteNLQFulfilled) &&
                    !conversationLimitReached && (
                      <div className={styles.leftResult}>
                        {/* AESTHETIC: pill badge */}
                        <TurnLabel
                          variant="result"
                          style={{ marginRight: "8px" }}
                        >
                          Response
                        </TurnLabel>

                        {Message === undefined || Message === null ? (
                          <span
                            style={{
                              fontSize: "0.875rem",
                              fontWeight: 300,
                              color: "var(--active-bg-color)",
                            }}
                          >
                            No data available
                          </span>
                        ) : (
                          <span
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: "12px",
                              width: "100%",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "0.875rem",
                                fontWeight: 300,
                                color: "#333",
                                flex: 1,
                                whiteSpace: "pre-line",
                              }}
                            >
                              {Message == null ? (
                                "No data available"
                              ) : typeof Message === "object" ? (
                                <>
                                  {/* Running status */}
                                  {(Message as any).status?.toUpperCase() ===
                                    "RUNNING" && (
                                    <>
                                      {queryStopped ? (
                                        <span
                                          style={{
                                            fontSize: "0.875rem",
                                            fontWeight: 300,
                                            color: "#7a5c00",
                                            fontStyle: "italic",
                                          }}
                                        >
                                          This query was cancelled.
                                        </span>
                                      ) : (
                                        <>
                                          {!isStopInProgress &&
                                            (() => {
                                              const filterStr =
                                                (Message as any).filter_str ??
                                                (Message as any).nl_query_params
                                                  ?.filter_str;
                                              return filterStr?.length > 0 ? (
                                                <div>
                                                  {filterStr.join("\n")}
                                                </div>
                                              ) : null;
                                            })()}

                                          {isStopInProgress ? (
                                            <div style={{ color: "#7a5c00" }}>
                                              Stopping query, please wait…
                                            </div>
                                          ) : (
                                            <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "0.875rem", fontWeight: 300, marginTop: "6px" }}>
                                              <div className={styles.inlineLoadingSpinner} style={{ flexShrink: 0 }} />
                                              <span key={processingWordIdx} style={{ animation: "wordFadeIn 0.35s ease forwards", display: "inline-block" }}>
                                                {processingWords[processingWordIdx]}
                                              </span>
                                              {(Message as any).completion_percentage > 0
                                                ? `… ${(Message as any).completion_percentage}% done.`
                                                : "…"}
                                            </div>
                                          )}
                                        </>
                                      )}
                                    </>
                                  )}
                                  {/* Result string (non-running) */}
                                  {(Message as any).status?.toUpperCase() !==
                                    "RUNNING" &&
                                    (
                                      Message as any
                                    ).nl_query_params?.result_string?.trim() && (
                                      <div>
                                        {renderResultWithQueryIdLink(
                                          stripRunningText(
                                            (Message as any).nl_query_params
                                              .result_string,
                                          )?.replace(/\n{2,}/g, "\n"),
                                        )}
                                      </div>
                                    )}
                                  {/* Fallback if result string is empty (non-running) */}
                                  {(Message as any).status?.toUpperCase() !==
                                    "RUNNING" &&
                                    !(
                                      Message as any
                                    ).nl_query_params?.result_string?.trim() && (
                                      <div>
                                        Query is{" "}
                                        {(Message as any).status
                                          ? (Message as any).status
                                              .charAt(0)
                                              .toUpperCase() +
                                            (Message as any).status
                                              .slice(1)
                                              .toLowerCase()
                                          : "Unknown"}
                                        ! Please check Query ID:{" "}
                                        {(Message as any).id} in{" "}
                                        {campaignReportsLabel} for more details.
                                      </div>
                                    )}
                                </>
                              ) : typeof Message === "string" ? (
                                (() => {
                                  try {
                                    const parsed = JSON.parse(Message);
                                    return parsed?.nl_query_params?.result_string?.trim()
                                      ? stripRunningText(
                                          parsed.nl_query_params.result_string,
                                        )?.replace(/\n{2,}/g, "\n")
                                      : Message;
                                  } catch {
                                    return Message;
                                  }
                                })()
                              ) : (
                                Message
                              )}
                            </span>
                            {typeof Message === "object" &&
                              (Message as any).status?.toLowerCase() ===
                                "running" &&
                              !isStopInProgress &&
                              !queryStopped &&
                              !isSelectedQueryCancelled && (
                                <div className={styles.queryActionGroup}>
                                  <div className={styles.queryActionRow}>
                                    <Tooltip text="Refresh to get latest results" placement="top">
                                      <button
                                        className={styles.queryRefreshBtn}
                                        disabled={isRefreshing}
                                        onClick={async () => {
                                          if (isRefreshingRef.current) return;
                                          isRefreshingRef.current = true;
                                          setIsRefreshing(true);
                                          try {
                                            const res = await getQueryById({
                                              args: {
                                                organizationId: orgId,
                                                query_id: currentQueryId,
                                              },
                                            });
                                            if (res) setMessage(res);
                                          } catch {
                                            // ignore transient errors; user can retry
                                          } finally {
                                            isRefreshingRef.current = false;
                                            setIsRefreshing(false);
                                          }
                                        }}
                                      >
                                        <MdOutlineAutorenew
                                          size={13}
                                          style={{
                                            animation: isRefreshing
                                              ? "spin 0.8s linear infinite"
                                              : undefined,
                                            transition: "opacity 0.2s ease",
                                            opacity: isRefreshing ? 1 : 0.6,
                                          }}
                                        />
                                        {isRefreshing
                                          ? "Refreshing…"
                                          : "Refresh"}
                                      </button>
                                    </Tooltip>
                                    <Tooltip text={deleteQueryFn.isPending ? "Stopping…" : "Cancel this query"} placement="top">
                                      <button
                                        className={styles.queryCancelBtn}
                                        disabled={deleteQueryFn.isPending}
                                        onClick={() => {
                                          const queryId =
                                            (currentQueryId ?? selectedQueryId) as string | null ?? null;
                                          setCancelConfirmQueryId(queryId);
                                          setShowCancelConfirm(true);
                                        }}
                                      >
                                        {deleteQueryFn.isPending
                                          ? "Stopping…"
                                          : "Cancel"}
                                      </button>
                                    </Tooltip>
                                  </div>
                                  {renderLastRefreshedAt(
                                    (Message as any).id ??
                                      currentQueryId ??
                                      selectedQueryId,
                                    true,
                                  )}
                                </div>
                              )}
                          </span>
                        )}
                      </div>
                    )}

                  {/* AESTHETIC: hairline separator closes the conversation area cleanly */}
                  <div
                    style={{
                      borderTop: "1px solid #f0f2f5",
                      margin: "4px 0 0",
                    }}
                  />
                  <div ref={conversationBottomRef} />
                </div>

                {/* Input bar */}
                {!isViewOnly && (
                  <div className={styles.askAiSection}>
                    {selectedQueryId &&
                      (() => {
                        const turnsDone = [
                          ...(deduplicatedHistory.find(
                            (d) => d?.id === selectedQueryId,
                          )?.past_prompts_per_conversation_id ?? []),
                        ].length;
                        const maxTurns = selectedModel?.maxTurns ?? 5;
                        const isOver =
                          conversationLimitReached ||
                          turnsDone >= maxTurns ||
                          isSelectedQueryFailed;
                        return (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              fontSize: "0.75rem",
                              color: isOver ? "#922b21" : "#333",
                              background: isOver ? "#fdf2f2" : "var(--Primary-Color-Shade-1-pale)",
                              border: isOver
                                ? "1px solid #f0c4c4"
                                : "1px solid color-mix(in srgb, var(--Primary-Color-Shade-1-pale) 60%, #d7dbdf)",
                              borderRadius: "6px",
                              padding: "5px 10px",
                              marginBottom: "8px",
                            }}
                          >
                            {isOver ? (
                              <>
                                <span>🚫</span>
                                <span>
                                  {isSelectedQueryFailed
                                    ? "This query failed. New queries cannot be submitted in this conversation. "
                                    : `This conversation is over — the maximum ${maxTurns} turn${maxTurns !== 1 ? "s" : ""} have been used. New queries cannot be submitted here. `}
                                  <button
                                    onClick={() => {
                                      setSelectedQueryId(null);
                                      setSelectedConversationId(null);
                                      setSelectedResults([]);
                                      setSelectedStatus("");
                                      setSelectedInquirerBucketId(null);
                                      setConversationLimitReached(false);
                                      setCurrentInputText(undefined);
                                      setDraftError(null);
                                      setTimeout(
                                        () => queryInputRef.current?.focus(),
                                        0,
                                      );
                                    }}
                                    style={{
                                      fontSize: "0.75rem",
                                      fontWeight: 600,
                                      color: "#922b21",
                                      background: "none",
                                      border: "none",
                                      cursor: "pointer",
                                      textDecoration: "underline",
                                      padding: 0,
                                    }}
                                  >
                                    Start a new one
                                  </button>
                                </span>
                              </>
                            ) : (
                              <>
                                <span>💬</span>
                                <span>
                                  Viewing a past conversation — type a new query
                                  below to continue it.
                                  {maxTurns
                                    ? ` (${turnsDone}/${maxTurns} turns used)`
                                    : ""}
                                </span>
                              </>
                            )}
                          </div>
                        );
                      })()}
                    <div className={styles.bottom}>
                      <div className={styles.leftInput}>
                        <Tooltip
                          text={
                            hasRunningQuery
                              ? "Please wait for the current query to finish running before starting a new conversation."
                              : hasQueryError
                                ? "Clear the error above before submitting a new query."
                                : queryStopped
                                  ? "Clear the stopped query above before submitting a new one."
                                  : isSelectedQueryCancelled
                                    ? "This query was cancelled — start a new chat to continue."
                                    : isSelectedQueryFailed
                                      ? "This query failed — start a new chat to continue."
                                      : conversationLimitReached
                                        ? "Conversation limit reached — start a new conversation."
                                        : orgQueryLimitReached
                                          ? "Organization has reached its query limit for this query objective. Contact administrator."
                                          : undefined
                          }
                          placement="top"
                          style={{ flex: 1, minWidth: 0 }}
                        >
                        <span
                          className={styles.searchBar}
                          style={
                            hasRunningQuery ||
                            hasQueryError ||
                            queryStopped ||
                            isSelectedQueryCancelled ||
                            isSelectedQueryFailed ||
                            conversationLimitReached ||
                            orgQueryLimitReached
                              ? { opacity: 0.45, cursor: "not-allowed" }
                              : undefined
                          }
                        >
                          <span style={{ flex: 1, minWidth: 0 }}>
                            <input
                              ref={queryInputRef}
                              type="text"
                              placeholder="Type your query"
                              value={postContent}
                              onChange={(e) => setPostContent(e.target.value)}
                              onKeyUp={handleKeypress}
                              disabled={
                                hasRunningQuery ||
                                hasQueryError ||
                                queryStopped ||
                                isSelectedQueryCancelled ||
                                isSelectedQueryFailed ||
                                conversationLimitReached ||
                                orgQueryLimitReached
                              }
                              style={{
                                width: "100%",
                                ...((hasRunningQuery ||
                                  hasQueryError ||
                                  queryStopped ||
                                  isSelectedQueryCancelled ||
                                  isSelectedQueryFailed ||
                                  conversationLimitReached ||
                                  orgQueryLimitReached) && {
                                  pointerEvents: "none",
                                }),
                              }}
                            />
                          </span>
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "14px",
                            }}
                          >
                            <ModelButton
                              type="button"
                              onClick={() =>
                                modelDropdownOpen
                                  ? closeModelDropdown()
                                  : setModelDropdownOpen(true)
                              }
                            >
                              {selectedModel
                                ? selectedModel.displayName
                                : "Model"}
                              {FEATURES.curationSkin ? <OI n="down" size={12} /> : (
<svg
                                width="10"
                                height="6"
                                viewBox="0 0 10 6"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M1 1l4 4 4-4"
                                  stroke="var(--active-bg-color)"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
)}
                              {modelDropdownOpen && (
                                <ModelDropdown closing={modelDropdownClosing}>
                                  {llmModels.map((m, i) => (
                                    <ModelOption
                                      key={i}
                                      selected={m === selectedModel}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedModel(m);
                                        closeModelDropdown();
                                      }}
                                    >
                                      {m.displayName}
                                    </ModelOption>
                                  ))}
                                </ModelDropdown>
                              )}
                            </ModelButton>
                            {!hasRunningQuery && (
                              <Tooltip
                                text={
                                  orgQueryLimitReached
                                    ? "Organization has reached its query limit for this query objective. Contact administrator."
                                    : !postContent?.trim() ||
                                      !inputValue?.name?.trim()
                                      ? "Please enter your query and a cohort name to submit"
                                      : !selectedModel
                                        ? "Please select a model"
                                        : undefined
                                }
                                placement="top"
                              >
                              <SubmitButton
                                active={
                                  !hasQueryError &&
                                  !queryStopped &&
                                  !isSelectedQueryCancelled &&
                                  !isSelectedQueryFailed &&
                                  !conversationLimitReached &&
                                  !orgQueryLimitReached &&
                                  !!(
                                    postContent?.trim() &&
                                    inputValue?.name?.trim() &&
                                    selectedModel
                                  )
                                }
                                style={
                                  hasRunningQuery ||
                                  hasQueryError ||
                                  queryStopped ||
                                  isSelectedQueryCancelled ||
                                  isSelectedQueryFailed ||
                                  conversationLimitReached ||
                                  orgQueryLimitReached ||
                                  !postContent?.trim() ||
                                  !inputValue?.name?.trim() ||
                                  !selectedModel
                                    ? { pointerEvents: "none" }
                                    : undefined
                                }
                                onClick={() => {
                                  if (
                                    hasRunningQuery ||
                                    hasQueryError ||
                                    queryStopped ||
                                    isSelectedQueryCancelled ||
                                    isSelectedQueryFailed ||
                                    conversationLimitReached ||
                                    orgQueryLimitReached ||
                                    !postContent?.trim() ||
                                    !inputValue?.name?.trim() ||
                                    !selectedModel
                                  )
                                    return;
                                  handleAISubmit(selectedModel);
                                }}
                              >
                                <FaArrowUp size={16} />
                              </SubmitButton>
                              </Tooltip>
                            )}
                          </span>
                        </span>
                        </Tooltip>
                        {hasRunningQuery && (
                          <Tooltip
                            text={
                              queryStopped
                                ? "Query already stopped"
                                : deleteQueryFn.isPending
                                  ? "Stopping…"
                                  : "Stop this query"
                            }
                            placement="top"
                          >
                          <StopButton
                            disabled={deleteQueryFn.isPending || queryStopped}
                            style={{ marginLeft: "8px" }}
                            onClick={async () => {
                              const queryId = currentQueryId ?? selectedQueryId;
                              if (!queryId) {
                                setPendingStop(true);
                                return;
                              }
                              try {
                                await deleteQueryFn.run({
                                  QueryId: queryId,
                                  organizationId: orgId,
                                });
                                setQueryStopped(true);
                                setLocallyStoppedQueryId(queryId);
                              } catch {
                                // stop failed silently
                              }
                            }}
                          >
                            <div
                              style={{
                                width: 14,
                                height: 14,
                                background: "white",
                                borderRadius: 2,
                              }}
                            />
                          </StopButton>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#9ca3af",
                        fontStyle: "italic",
                        marginTop: "6px",
                      }}
                    >
                      Default objective: Activation
                    </div>
                  </div>
                )}
              </div>

              {/* View-only overlay — covers the entire container */}
              {isViewOnly &&
                !selectedResults.length &&
                !selectedStatus &&
                !currentInputText && (
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      backdropFilter: "blur(5px)",
                      WebkitBackdropFilter: "blur(5px)",
                      background: "rgba(255,255,255,0.6)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "16px",
                      zIndex: 10,
                      borderRadius: "10px",
                      padding: "32px",
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        background: "white",
                        borderRadius: "12px",
                        border: "1px solid #e2e8f0",
                        boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
                        padding: "32px 40px",
                        maxWidth: "480px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                        alignItems: "center",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "20px",
                          fontFamily: "Montserrat, sans-serif",
                          fontWeight: 700,
                          color: "#333",
                          letterSpacing: "0.01em",
                        }}
                      >
                        Ask MITRA — View Mode
                      </span>
                      <span
                        style={{
                          fontSize: "14px",
                          fontFamily: "Roboto, sans-serif",
                          color: "#6f7a85",
                          lineHeight: 1.6,
                        }}
                      >
                        Select any conversation from{" "}
                        <strong style={{ color: "#333" }}>Your chats</strong>{" "}
                        on the right to view it in detail.
                      </span>
                      <div
                        style={{
                          width: "100%",
                          borderTop: "1px solid #e8eaed",
                          margin: "4px 0",
                        }}
                      />
                      <span
                        style={{
                          fontSize: "13px",
                          fontFamily: "Roboto, sans-serif",
                          color: "#6f7a85",
                        }}
                      >
                        To run new queries, go to{" "}
                        <button
                          onClick={() => history.push("/campaign-planning")}
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            color: "var(--Primary-Color-Shade-1)",
                            fontFamily: "inherit",
                            fontSize: "inherit",
                            cursor: "pointer",
                            textDecoration: "underline",
                            fontWeight: 600,
                          }}
                        >
                          {campaignPlanningLabel}
                        </button>{" "}
                        and select attributes first.
                      </span>
                    </div>
                  </div>
                )}
            </div>
          </div>

          {/* ── Right column (chat history) ── */}
          {/* AESTHETIC: tinted background separates history panel from main workspace */}
          <div
            className={styles.rightContainer}
            style={{ background: "var(--Primary-Color-Shade-1-pale, #f4f5f6)" }}
          >
            <span
              style={{
                borderBottom: "1px solid #e5e7eb",
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                paddingBottom: "8px",
              }}
              className={styles.headerText}
            >
              Your chats
              <span
                style={{ display: "flex", alignItems: "center", gap: "4px" }}
              >
                <Tooltip text="New chat" placement="top">
                  <button
                    onClick={() => {
                      setShowAIResult(false);
                      setSelectedResults([]);
                      setSelectedStatus("");
                      setSelectedQueryId(null);
                      setSelectedConversationId(null);
                      setSelectedInquirerBucketId(null);
                      setCurrentInputText(undefined);
                      setConversationLimitReached(false);
                      setMessage(undefined);
                      setPostContent("");
                      setQueryStopped(false);
                      setPendingStop(false);
                      setTableCollapsed(true);
                      setDraftError(null);
                      setTimeout(() => queryInputRef.current?.focus(), 0);
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "2px 4px",
                      color: "#6f7a85",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <FaPen size={11} />
                  </button>
                </Tooltip>
                <Tooltip text="Refresh previous queries" placement="top">
                  <button
                    onClick={reloadHistory}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "2px 4px",
                      color: "#6f7a85",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <FaRedo size={12} />
                  </button>
                </Tooltip>
              </span>
            </span>

            <div
              style={{
                flexDirection: "column",
                display: "flex",
                width: "100%",
                flex: 1,
              }}
              className={styles.paddingLeftSection}
            >
              {/* ── History list: deduplicated to one entry per conversation ── */}
              {getGenerativeHistoryFn.isPending ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                    padding: "4px 0",
                  }}
                >
                  <style>{`@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      style={{
                        height: "44px",
                        borderRadius: "6px",
                        background:
                          "linear-gradient(90deg, #eaeef2 25%, #d7dbdf 50%, #eaeef2 75%)",
                        backgroundSize: "200% 100%",
                        animation: "shimmer 1.4s infinite",
                        opacity: 0.7,
                      }}
                    />
                  ))}
                </div>
              ) : deduplicatedHistory.length > 0 ? (
                deduplicatedHistory.map((data) => {
                  const isActive = data?.id === selectedQueryId;
                  const turnCount =
                    data.past_prompts_per_conversation_id?.length ?? 0;
                  const isMultiTurn = turnCount > 1;
                  const sortedTurns = [
                    ...(data.past_prompts_per_conversation_id ?? []),
                  ].sort((a, b) => (a.turn ?? 0) - (b.turn ?? 0));

                  return (
                    <Tooltip
                      key={data?.id}
                      text={
                        <span style={{ whiteSpace: "pre-wrap" }}>
                          {isMultiTurn
                            ? sortedTurns
                                .map((t) => `${t.turn}. ${t.prompt}`)
                                .join("\n")
                            : data.nl_query_params.input_text}
                        </span>
                      }
                      placement="top"
                      maxWidth={280}
                    >
                    <button
                      className={styles.historyTag}
                      onClick={() =>
                        addAllOrganizations(
                          data.nl_query_params.input_text,
                          [...(data.past_prompts_per_conversation_id ?? [])]
                            .sort((a, b) => (a.turn ?? 0) - (b.turn ?? 0))
                            .map((item) => ({
                              ...item,
                              filter_str: item.filter_str ?? data.filter_str,
                            })),
                          data.status,
                          data?.id,
                          data?.conversation_id,
                          data?.inquirer_bucket_id,
                          data?.campaign_name,
                          data?.campaign_id,
                          data,
                        )
                      }
                      style={{
                        borderLeft: isActive
                          ? "3px solid var(--Primary-Color-Shade-1)"
                          : isMultiTurn
                            ? "3px solid var(--Primary-Color-Shade-1-pale)"
                            : "3px solid transparent",
                        paddingLeft:
                          isActive || isMultiTurn ? "10px" : undefined,
                        background: isActive
                          ? "var(--Primary-Color-Shade-1-pale)"
                          : undefined,
                        transition: "border-left 0.15s, background 0.15s",
                        opacity: isMultiTurn ? 1 : 0.82,
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive) {
                          (e.currentTarget as HTMLElement).style.background =
                            "var(--Primary-Color-Shade-1-pale)";
                          (e.currentTarget as HTMLElement).style.borderLeft =
                            "3px solid var(--Primary-Color-Shade-1)";
                        }
                        const textEl = (
                          e.currentTarget as HTMLElement
                        ).querySelector("[data-query-text]") as HTMLElement;
                        if (textEl) {
                          textEl.style.whiteSpace = "normal";
                          textEl.style.overflow = "visible";
                          textEl.style.textOverflow = "unset";
                          textEl.style.wordBreak = "break-word";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) {
                          (e.currentTarget as HTMLElement).style.background =
                            "transparent";
                          (e.currentTarget as HTMLElement).style.borderLeft =
                            isMultiTurn
                              ? "3px solid var(--Primary-Color-Shade-1-pale)"
                              : "3px solid transparent";
                        }
                        const textEl = (
                          e.currentTarget as HTMLElement
                        ).querySelector("[data-query-text]") as HTMLElement;
                        if (textEl) {
                          textEl.style.whiteSpace = "nowrap";
                          textEl.style.overflow = "hidden";
                          textEl.style.textOverflow = "ellipsis";
                          textEl.style.wordBreak = "normal";
                        }
                      }}
                    >
                      <span
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "2px",
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        <span
                          className={styles.historyTagText}
                          data-query-text="true"
                          style={{ fontWeight: isMultiTurn ? 500 : 400 }}
                        >
                          {data.nl_query_params.input_text}
                        </span>
                        {isMultiTurn && (
                          <span
                            style={{
                              fontSize: "11px",
                              color: "#333",
                              fontFamily: "Roboto, sans-serif",
                              display: "flex",
                              flexDirection: "column",
                              gap: "3px",
                            }}
                          >
                            <span
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "3px",
                              }}
                            >
                              <span
                                style={{
                                  width: "5px",
                                  height: "5px",
                                  borderRadius: "50%",
                                  background: "var(--Primary-Color-Shade-1)",
                                  opacity: 0.5,
                                  flexShrink: 0,
                                  display: "inline-block",
                                }}
                              />
                              {turnCount} turns
                            </span>
                            <span
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "2px",
                                paddingLeft: "8px",
                                borderLeft: "2px solid var(--Primary-Color-Shade-1-pale)",
                                marginLeft: "2px",
                              }}
                            >
                              {sortedTurns.map((turn, idx) => (
                                <Tooltip
                                  key={idx}
                                  text={turn.prompt}
                                  placement="top"
                                  maxWidth={240}
                                >
                                <span
                                  style={{
                                    fontSize: "11px",
                                    color: "#333",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    maxWidth: "160px",
                                    cursor: "default",
                                    padding: "2px 4px",
                                    borderRadius: "4px",
                                    transition: "background 0.15s, color 0.15s",
                                  }}
                                  onMouseEnter={(e) => {
                                    (
                                      e.currentTarget as HTMLElement
                                    ).style.background = "var(--Primary-Color-Shade-1-pale)";
                                    (
                                      e.currentTarget as HTMLElement
                                    ).style.color = "var(--Primary-Color-Shade-1)";
                                    (
                                      e.currentTarget as HTMLElement
                                    ).style.overflow = "visible";
                                    (
                                      e.currentTarget as HTMLElement
                                    ).style.whiteSpace = "normal";
                                    (
                                      e.currentTarget as HTMLElement
                                    ).style.maxWidth = "none";
                                  }}
                                  onMouseLeave={(e) => {
                                    (
                                      e.currentTarget as HTMLElement
                                    ).style.background = "transparent";
                                    (
                                      e.currentTarget as HTMLElement
                                    ).style.color = "var(--Primary-Color-Shade-1)";
                                    (
                                      e.currentTarget as HTMLElement
                                    ).style.overflow = "hidden";
                                    (
                                      e.currentTarget as HTMLElement
                                    ).style.whiteSpace = "nowrap";
                                    (
                                      e.currentTarget as HTMLElement
                                    ).style.maxWidth = "160px";
                                  }}
                                >
                                  {turn.turn}. {turn.prompt}
                                </span>
                                </Tooltip>
                              ))}
                            </span>
                          </span>
                        )}
                      </span>
                      <ExpandIcon
                        width={18}
                        height={18}
                        style={{ flexShrink: 0, color: "#6f7a85" }}
                      />
                    </button>
                    </Tooltip>
                  );
                })
              ) : (
                <span className={styles.emptyHistoryMessage}>
                  No previous queries yet. Submit a query to see your history
                  here.
                </span>
              )}
            </div>

            {/* ── Pagination — uses raw generativeHistory for cursor logic ── */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
                marginTop: "auto",
                paddingTop: "8px",
                borderTop: "1px solid #e5e7eb",
              }}
            >
              {generativeHistory !== undefined &&
              generativeHistory.length > 0 ? (
                <>
                  <button
                    onClick={previousHistory}
                    disabled={pageKeyStack.length <= 1}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: pageKeyStack.length <= 1 ? "default" : "pointer",
                      fontWeight: 500,
                      fontSize: "11px",
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      fontFamily: "Roboto, sans-serif",
                      color: pageKeyStack.length <= 1 ? "#b7bec6" : "#333",
                      padding: "4px 0",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    ← Previous
                  </button>
                  <button
                    onClick={() =>
                      nextHistory(
                        generativeHistory[generativeHistory.length - 1]
                          .created_at,
                      )
                    }
                    disabled={generativeHistory.length < 7}
                    style={{
                      background: "none",
                      border: "none",
                      cursor:
                        generativeHistory.length < 7 ? "default" : "pointer",
                      fontWeight: 500,
                      fontSize: "11px",
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      fontFamily: "Roboto, sans-serif",
                      color:
                        generativeHistory.length < 7 ? "#b7bec6" : "var(--active-bg-color)",
                      padding: "4px 0",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    Next →
                  </button>
                </>
              ) : null}
            </div>
          </div>
        </div>
      </LayoutContainer>

      <InfoPopup
        isOpen={showCancelConfirm}
        warning
        headerText="Cancel Query"
        displayText="Are you sure you want to cancel this query? This action cannot be undone."
        freeText
        closeButtonLabel="Yes, Cancel"
        showCancelButton
        onCancelButtonClick={() => {
          setShowCancelConfirm(false);
          setCancelConfirmQueryId(null);
        }}
        onClose={async () => {
          setShowCancelConfirm(false);
          const queryId = cancelConfirmQueryId;
          setCancelConfirmQueryId(null);
          if (!queryId) {
            setPendingStop(true);
            return;
          }
          try {
            await deleteQueryFn.run({
              QueryId: queryId,
              organizationId: orgId,
            });
            setQueryStopped(true);
            setLocallyStoppedQueryId(queryId);
          } catch {
            // stop failed silently
          }
        }}
      />
    </>
  );
};

export default DiscoverAi;
